import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, Plus, Play, Eye, Heart, X, Volume2, Maximize2, Upload, Video, Loader2, Film, Trash2, Edit, Image, RefreshCw, Pause, VolumeX, Link, Youtube, Pencil, Check, Music, Camera, SkipBack, SkipForward, Download, DollarSign, Tag, Clock, Hash, Headphones, Guitar, Smile, ChevronDown, Activity, Sparkles, Wand2, Share2, ExternalLink, Copy, Calendar, ListMusic, List, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import { useUserId } from '../context/UserAuthContext';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { analyzeAudio, detectBPMOnly } from '../utils/audioAnalysis';
import { FastImage, VideoThumbnail } from '../components/OptimizedImage';
import OptimizedImage from '../components/OptimizedImage';

// API URL - dynamically determine at runtime based on window location
const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const RealEstateMediaPortfolio = () => {
  const userId = useUserId();
  
  // Global audio player context
  const { 
    currentTrack: currentlyPlaying, 
    isPlaying: audioIsPlaying, 
    playTrack: globalPlayTrack,
    setPlaylist,
    stopPlayback
  } = useAudioPlayer();
  
  // Main media type tabs: Videos, Photos, Audio
  const [mainMediaTab, setMainMediaTab] = useState('Videos');
  // Collapse state for the Story and Full Library sections — persisted in
  // localStorage so the user's choice survives reloads. Default to expanded.
  const [storyCollapsed, setStoryCollapsed] = useState(() => {
    try { return localStorage.getItem('media_portfolio_story_collapsed') === '1'; } catch { return false; }
  });
  const [libraryCollapsed, setLibraryCollapsed] = useState(() => {
    try { return localStorage.getItem('media_portfolio_library_collapsed') === '1'; } catch { return false; }
  });
  const toggleStoryCollapsed = () => {
    setStoryCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('media_portfolio_story_collapsed', next ? '1' : '0'); } catch { /* ignored */ }
      return next;
    });
  };
  const toggleLibraryCollapsed = () => {
    setLibraryCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('media_portfolio_library_collapsed', next ? '1' : '0'); } catch { /* ignored */ }
      return next;
    });
  };
  
  const [activeTab, setActiveTab] = useState('All');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Video upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState('story');
  const [uploadSource, setUploadSource] = useState('file'); // 'file' or 'youtube'
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [videoCategory, setVideoCategory] = useState('Interviews');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedThumbnailFile, setSelectedThumbnailFile] = useState(null);
  const [generatedThumbnail, setGeneratedThumbnail] = useState(null);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editType, setEditType] = useState('');
  const [editYoutubeUrl, setEditYoutubeUrl] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [newThumbnailFile, setNewThumbnailFile] = useState(null);
  const [newVideoFile, setNewVideoFile] = useState(null);
  
  // Videos from API
  const [storyVideos, setStoryVideos] = useState([]);
  const [libraryVideos, setLibraryVideos] = useState([]);
  
  // Drag state for reordering videos
  const [draggedVideo, setDraggedVideo] = useState(null);
  const [dragOverVideo, setDragOverVideo] = useState(null);
  const [dragSection, setDragSection] = useState(null); // 'story' or 'library'
  const [isLoading, setIsLoading] = useState(true);
  
  // Video player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const videoPlayerRef = useRef(null);
  
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const editThumbnailRef = useRef(null);
  const editVideoRef = useRef(null);
  const previewVideoRef = useRef(null);
  
  // Audio state for track list
  const [audioTracks, setAudioTracks] = useState([]);
  const [audioLoading, setAudioLoading] = useState(false);
  const [showAudioUploadModal, setShowAudioUploadModal] = useState(false);
  const [showAudioEditModal, setShowAudioEditModal] = useState(false);
  const [editingTrack, setEditingTrack] = useState(null);
  const [audioSearchQuery, setAudioSearchQuery] = useState('');
  const [draggedTrackIndex, setDraggedTrackIndex] = useState(null);
  
  // Edit track form state
  const [editTrackTitle, setEditTrackTitle] = useState('');
  const [editTrackArtist, setEditTrackArtist] = useState('');
  const [editTrackBpm, setEditTrackBpm] = useState('');
  const [editTrackKey, setEditTrackKey] = useState('');
  const [editTrackTags, setEditTrackTags] = useState('');
  const [editTrackPrice, setEditTrackPrice] = useState('');
  const [editTrackIsFree, setEditTrackIsFree] = useState(true);
  const [editTrackCategory, setEditTrackCategory] = useState('Beats');
  const [editTrackGenre, setEditTrackGenre] = useState('');
  const [editTrackMood, setEditTrackMood] = useState('');
  const [editTrackInstrument, setEditTrackInstrument] = useState('');
  const [editTrackType, setEditTrackType] = useState('');
  const [editTrackPublishedDate, setEditTrackPublishedDate] = useState('');
  const [editCoverFile, setEditCoverFile] = useState(null);
  const [editCoverPreview, setEditCoverPreview] = useState(null);
  const [editAudioFile, setEditAudioFile] = useState(null);
  const [editAudioFileName, setEditAudioFileName] = useState(null);
  const [uploadingEditAudio, setUploadingEditAudio] = useState(false);
  const [isUpdatingTrack, setIsUpdatingTrack] = useState(false);
  const [detectingBpm, setDetectingBpm] = useState(false);
  const [detectingKey, setDetectingKey] = useState(false);
  const [detectingUploadKey, setDetectingUploadKey] = useState(false);
  const [audioTagFilter, setAudioTagFilter] = useState('');
  const [allAudioTags, setAllAudioTags] = useState([]);
  const [audioPage, setAudioPage] = useState(1);
  const TRACKS_PER_PAGE = 100;
  const VISIBLE_TRACKS = 12;
  const audioInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const editCoverInputRef = useRef(null);
  const editAudioInputRef = useRef(null);
  
  // Audio upload form state
  const [audioTitle, setAudioTitle] = useState('');
  const [audioArtist, setAudioArtist] = useState('');
  const [audioBpm, setAudioBpm] = useState('');
  const [audioKey, setAudioKey] = useState('');
  const [audioTags, setAudioTags] = useState('');
  const [audioPrice, setAudioPrice] = useState('');
  const [audioIsFree, setAudioIsFree] = useState(true);
  const [audioCategory, setAudioCategory] = useState('Beats');
  const [audioGenre, setAudioGenre] = useState('');
  const [audioMood, setAudioMood] = useState('');
  const [audioInstrument, setAudioInstrument] = useState('');
  const [audioTrackType, setAudioTrackType] = useState('');
  const [audioPublishedDate, setAudioPublishedDate] = useState('');
  const [selectedAudioFile, setSelectedAudioFile] = useState(null);
  const [selectedCoverFile, setSelectedCoverFile] = useState(null);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [detectingUploadBpm, setDetectingUploadBpm] = useState(false);
  const [generatingUploadCover, setGeneratingUploadCover] = useState(false);
  const [generatedUploadCoverPreview, setGeneratedUploadCoverPreview] = useState(null);
  const [generatingEditCover, setGeneratingEditCover] = useState(false);
  // Bulk operations state
  const [selectedTrackIds, setSelectedTrackIds] = useState(new Set());
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const bulkUploadInputRef = useRef(null);
  const [bulkUploadFiles, setBulkUploadFiles] = useState([]); // [{file, status, error}]
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [bulkArtist, setBulkArtist] = useState('');
  const [bulkGenre, setBulkGenre] = useState('');
  const [bulkMood, setBulkMood] = useState('');
  const [bulkCategory, setBulkCategory] = useState('Beats');
  const [bulkIsFree, setBulkIsFree] = useState(true);
  const [bulkPrice, setBulkPrice] = useState('');
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkEditing, setIsBulkEditing] = useState(false);
  const [bulkEditGenre, setBulkEditGenre] = useState('');
  const [bulkEditMood, setBulkEditMood] = useState('');
  const [bulkEditCategory, setBulkEditCategory] = useState('');
  const [bulkEditIsFree, setBulkEditIsFree] = useState('');
  const [bulkEditPrice, setBulkEditPrice] = useState('');
  const [bulkEditTags, setBulkEditTags] = useState('');
  const [bulkEditArtist, setBulkEditArtist] = useState('');

  // Playlist state
  const [audioSubTab, setAudioSubTab] = useState('tracks'); // 'tracks' | 'playlists'
  const [audioViewMode, setAudioViewMode] = useState(() => {
    // Persist view mode preference in localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem('audioViewMode') || 'list';
    }
    return 'list';
  }); // 'list' | 'grid' - view mode for audio tracks
  const [playlists, setPlaylists] = useState([]);
  const [playlistsLoading, setPlaylistsLoading] = useState(false);
  const [showCreatePlaylistModal, setShowCreatePlaylistModal] = useState(false);
  const [showEditPlaylistModal, setShowEditPlaylistModal] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [playlistName, setPlaylistName] = useState('');
  const [playlistDescription, setPlaylistDescription] = useState('');
  const [playlistTrackSearch, setPlaylistTrackSearch] = useState('');
  const [playlistSelectedTracks, setPlaylistSelectedTracks] = useState([]); // ordered list of track ids
  const [isSavingPlaylist, setIsSavingPlaylist] = useState(false);
  const [playlistLinkCopied, setPlaylistLinkCopied] = useState(null); // playlist id whose link was just copied
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [showPhotoEditModal, setShowPhotoEditModal] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [selectedPhotoFiles, setSelectedPhotoFiles] = useState([]);
  const [photoTitle, setPhotoTitle] = useState('');
  const [photoCaption, setPhotoCaption] = useState('');
  const [photoTags, setPhotoTags] = useState('');
  const [photoCategory, setPhotoCategory] = useState('General');
  const [photoAlbum, setPhotoAlbum] = useState('');
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [photoSearchQuery, setPhotoSearchQuery] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);
  const [activePhotoCategoryFilter, setActivePhotoCategoryFilter] = useState('All');
  const [editPhotoTitle, setEditPhotoTitle] = useState('');
  const [editPhotoCaption, setEditPhotoCaption] = useState('');
  const [editPhotoTags, setEditPhotoTags] = useState('');
  const [editPhotoCategory, setEditPhotoCategory] = useState('');
  const [isUpdatingPhoto, setIsUpdatingPhoto] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const photoInputRef = useRef(null);
  
  // Photo categories from database
  const [photoCategories, setPhotoCategories] = useState([]);
  const [photoCategoriesLoading, setPhotoCategoriesLoading] = useState(false);
  
  // Audio filter state - horizontal filters
  const [audioFilters, setAudioFilters] = useState({
    genre: '',
    trackType: '',
    price: '',
    mood: '',
    key: '',
    instrument: '',
    bpm: '',
    duration: ''
  });
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const [audioSortBy, setAudioSortBy] = useState('');
  
  // Filter options
  const filterOptions = {
    genre: ['Hip Hop', 'R&B', 'Pop', 'EDM', 'Rock', 'Jazz', 'Classical', 'Trap', 'Lo-Fi', 'Soul', 'House', 'Metal', 'Country', 'Rage', 'Afro Beat', 'Break Beat'],
    trackType: ['Beats', 'Instrumentals', 'Samples', 'Loops', 'Vocals', 'Sound Effects'],
    price: ['Free', 'Under $25', '$25-$50', '$50-$100', 'Over $100'],
    mood: ['Dark', 'Happy', 'Sad', 'Energetic', 'Chill', 'Aggressive', 'Romantic', 'Mysterious'],
    key: [
      // All 12 Major keys
      'C Major', 'C# Major', 'Db Major', 'D Major', 'D# Major', 'Eb Major', 
      'E Major', 'F Major', 'F# Major', 'Gb Major', 'G Major', 'G# Major', 
      'Ab Major', 'A Major', 'A# Major', 'Bb Major', 'B Major',
      // All 12 Minor keys
      'C Minor', 'C# Minor', 'Db Minor', 'D Minor', 'D# Minor', 'Eb Minor',
      'E Minor', 'F Minor', 'F# Minor', 'Gb Minor', 'G Minor', 'G# Minor',
      'Ab Minor', 'A Minor', 'A# Minor', 'Bb Minor', 'B Minor'
    ],
    instrument: ['Piano', 'Guitar', 'Synth', 'Drums', 'Bass', 'Strings', 'Brass', 'Flute'],
    bpm: ['60-80 BPM', '80-100 BPM', '100-120 BPM', '120-140 BPM', '140-160 BPM', '160+ BPM'],
    duration: ['Under 1 min', '1-2 min', '2-3 min', '3-5 min', 'Over 5 min']
  };

  // Default media categories
  const defaultMediaCategories = [
    { id: 'All', name: 'All', icon: '📋', color: 'bg-gray-600', order: 0 },
    { id: 'Interviews', name: 'Interviews', icon: '🎤', color: 'bg-blue-600', order: 1 },
    { id: 'Speeches', name: 'Speeches', icon: '🎙️', color: 'bg-purple-600', order: 2 },
    { id: 'Commercials', name: 'Commercials', icon: '📺', color: 'bg-pink-600', order: 3 },
    { id: 'Real Estate', name: 'Real Estate', icon: '🏠', color: 'bg-green-600', order: 4 }
  ];
  
  // Drag state for reordering categories
  const [draggedCategory, setDraggedCategory] = useState(null);
  const [dragOverCategory, setDragOverCategory] = useState(null);
  
  // Load custom media categories from DATABASE (with localStorage as fallback during load)
  const [customMediaCategories, setCustomMediaCategories] = useState(defaultMediaCategories);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  
  // Fetch categories from database on mount
  useEffect(() => {
    const fetchCategories = async () => {
      if (!userId) {
        setCategoriesLoading(false);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/api/portfolio-categories/?user_id=${userId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.categories && data.categories.length > 0) {
            // Transform to match frontend format - use label or name
            const dbCategories = data.categories.map(cat => ({
              id: cat.name || cat.label,
              name: cat.name || cat.label,
              dbId: cat.id,
              icon: cat.icon || '📁',
              color: cat.color || 'bg-gray-600',
              order: cat.order || 0,
              is_default: cat.is_default
            }));
            setCustomMediaCategories(dbCategories);
            localStorage.setItem('portfolio_categories', JSON.stringify(dbCategories));
          }
          // If no categories in DB, keep using defaults
        }
      } catch (error) {
        console.error('Error fetching categories from database:', error);
        // Fall back to localStorage
        try {
          const saved = localStorage.getItem('portfolio_categories');
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed && parsed.length > 0) {
              setCustomMediaCategories(parsed);
            }
          }
        } catch { /* use defaults */ }
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, [userId]);
  
  // Editable category state
  const [showMediaCategoryModal, setShowMediaCategoryModal] = useState(false);
  const [newMediaCategoryName, setNewMediaCategoryName] = useState('');
  const [newMediaCategoryIcon, setNewMediaCategoryIcon] = useState('🎬');
  const [newMediaCategoryColor, setNewMediaCategoryColor] = useState('bg-blue-600');
  const [editingMediaCategory, setEditingMediaCategory] = useState(null);
  const [editMediaCategoryValue, setEditMediaCategoryValue] = useState('');
  
  const mediaCategoryIcons = ['📋', '⭐', '🎤', '🎙️', '📺', '🏠', '🎬', '🎥', '📹', '🎞️', '📽️', '🎭', '🎪', '🎨', '🎵', '🎸', '🎹', '🎧', '📸', '📷'];
  const mediaCategoryColors = ['bg-gray-600', 'bg-yellow-500', 'bg-blue-600', 'bg-purple-600', 'bg-pink-600', 'bg-green-600', 'bg-red-600', 'bg-orange-600', 'bg-cyan-600', 'bg-indigo-600', 'bg-teal-600'];
  
  const addMediaCategory = async () => {
    if (!newMediaCategoryName.trim()) return;
    if (customMediaCategories.some(c => c.id === newMediaCategoryName)) { 
      toast.error('Category already exists'); 
      return; 
    }
    
    try {
      // Save to database
      const response = await fetch(`${API_URL}/api/portfolio-categories/?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newMediaCategoryName,
          icon: newMediaCategoryIcon,
          color: newMediaCategoryColor
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const newCat = { 
            id: newMediaCategoryName, 
            name: newMediaCategoryName, 
            dbId: data.category.id,
            icon: newMediaCategoryIcon, 
            color: newMediaCategoryColor,
            order: data.category.order || customMediaCategories.length
          };
          const updated = [...customMediaCategories, newCat];
          setCustomMediaCategories(updated);
          localStorage.setItem('portfolio_categories', JSON.stringify(updated));
          setNewMediaCategoryName('');
          setNewMediaCategoryIcon('🎬');
          setNewMediaCategoryColor('bg-blue-600');
          toast.success(`Added "${newMediaCategoryName}" category`);
        }
      } else {
        toast.error('Failed to save category');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Failed to add category');
    }
  };
  
  const updateMediaCategory = async (oldId, newName) => {
    if (!newName.trim()) return;
    
    const category = customMediaCategories.find(c => c.id === oldId);
    const dbId = category?.dbId;
    
    try {
      if (dbId) {
        // Update in database
        const response = await fetch(`${API_URL}/api/portfolio-categories/${dbId}?user_id=${userId}&name=${encodeURIComponent(newName)}`, {
          method: 'PUT'
        });
        
        if (!response.ok) {
          toast.error('Failed to update category in database');
          return;
        }
      }
      
      const updated = customMediaCategories.map(c => 
        c.id === oldId ? { ...c, id: newName, name: newName } : c
      );
      setCustomMediaCategories(updated);
      localStorage.setItem('portfolio_categories', JSON.stringify(updated));
      if (activeTab === oldId) setActiveTab(newName);

      // Patch in-memory videos so existing cards reflect the new name + colour
      // immediately, without waiting for the next page refresh. The live
      // categoryColors lookup will pick up the new colour automatically once
      // customMediaCategories above is committed.
      const oldName = category?.name || oldId;
      const renameVideoCategory = (v) =>
        v.category === oldName
          ? { ...v, category: newName, categoryColor: undefined, category_color: undefined }
          : v;
      setStoryVideos((prev) => prev.map(renameVideoCategory));
      setLibraryVideos((prev) => prev.map(renameVideoCategory));

      setEditingMediaCategory(null);
      setEditMediaCategoryValue('');
      toast.success(`Category renamed to "${newName}"`);
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    }
  };
  
  const deleteMediaCategory = async (categoryId) => {
    if (categoryId === 'All') { 
      toast.error('Cannot delete the "All" category'); 
      return; 
    }
    
    const category = customMediaCategories.find(c => c.id === categoryId);
    const dbId = category?.dbId || categoryId;
    
    try {
      // Delete from database
      const response = await fetch(`${API_URL}/api/portfolio-categories/${dbId}?user_id=${userId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete category');
        return;
      }
      
      const updated = customMediaCategories.filter(c => c.id !== categoryId);
      setCustomMediaCategories(updated);
      localStorage.setItem('portfolio_categories', JSON.stringify(updated));
      if (activeTab === categoryId) setActiveTab('All');
      toast.success('Category deleted');
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  // Drag and drop handlers for reordering categories
  const handleDragStart = (e, category) => {
    if (category.id === 'All') return; // Don't allow dragging "All"
    setDraggedCategory(category);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, category) => {
    e.preventDefault();
    if (category.id === 'All' || !draggedCategory || category.id === draggedCategory.id) return;
    setDragOverCategory(category.id);
  };

  const handleDragLeave = () => {
    setDragOverCategory(null);
  };

  const handleDrop = async (e, targetCategory) => {
    e.preventDefault();
    if (!draggedCategory || targetCategory.id === 'All' || targetCategory.id === draggedCategory.id) {
      setDraggedCategory(null);
      setDragOverCategory(null);
      return;
    }

    // Reorder categories
    const categories = [...customMediaCategories];
    const draggedIndex = categories.findIndex(c => c.id === draggedCategory.id);
    const targetIndex = categories.findIndex(c => c.id === targetCategory.id);

    // Remove dragged item and insert at target position
    const [removed] = categories.splice(draggedIndex, 1);
    categories.splice(targetIndex, 0, removed);

    // Update order values
    const updatedCategories = categories.map((cat, index) => ({
      ...cat,
      order: index
    }));

    setCustomMediaCategories(updatedCategories);
    localStorage.setItem('portfolio_categories', JSON.stringify(updatedCategories));

    // Save order to database
    try {
      const categoryIds = updatedCategories.map(c => c.id);
      await fetch(`${API_URL}/api/portfolio-categories/reorder?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category_ids: categoryIds })
      });
      toast.success('Categories reordered');
    } catch (error) {
      console.error('Error saving category order:', error);
    }

    setDraggedCategory(null);
    setDragOverCategory(null);
  };

  const handleDragEnd = () => {
    setDraggedCategory(null);
    setDragOverCategory(null);
  };

  // Video drag and drop handlers
  const handleVideoDragStart = (e, video, section) => {
    setDraggedVideo(video);
    setDragSection(section);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleVideoDragOver = (e, video, section) => {
    e.preventDefault();
    if (!draggedVideo || video.id === draggedVideo.id || section !== dragSection) return;
    setDragOverVideo(video.id);
  };

  const handleVideoDragLeave = () => {
    setDragOverVideo(null);
  };

  const handleVideoDrop = async (e, targetVideo, section) => {
    e.preventDefault();
    if (!draggedVideo || targetVideo.id === draggedVideo.id || section !== dragSection) {
      setDraggedVideo(null);
      setDragOverVideo(null);
      setDragSection(null);
      return;
    }

    const videos = section === 'story' ? [...storyVideos] : [...libraryVideos];
    const setVideos = section === 'story' ? setStoryVideos : setLibraryVideos;
    
    const draggedIndex = videos.findIndex(v => v.id === draggedVideo.id);
    const targetIndex = videos.findIndex(v => v.id === targetVideo.id);

    // Remove dragged item and insert at target position
    const [removed] = videos.splice(draggedIndex, 1);
    videos.splice(targetIndex, 0, removed);

    // Update order values
    const updatedVideos = videos.map((video, index) => ({
      ...video,
      order: index
    }));

    setVideos(updatedVideos);

    // Save order to backend
    try {
      const videoIds = updatedVideos.map(v => v.id);
      await fetch(`${API_URL}/api/media-storage/videos/reorder?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_ids: videoIds, section: section })
      });
      toast.success('Videos reordered');
    } catch (error) {
      console.error('Error saving video order:', error);
    }

    setDraggedVideo(null);
    setDragOverVideo(null);
    setDragSection(null);
  };

  const handleVideoDragEnd = () => {
    setDraggedVideo(null);
    setDragOverVideo(null);
    setDragSection(null);
  };

  // Use custom categories
  const categories = customMediaCategories.map(c => c.name);
  
  // Generate categoryColors from custom categories
  const categoryColors = customMediaCategories.reduce((acc, cat) => {
    acc[cat.id] = cat.color;
    return acc;
  }, {});

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openFilterDropdown && !e.target.closest('.relative')) {
        setOpenFilterDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openFilterDropdown]);

  useEffect(() => {
    fetchVideos();
  }, [userId]);

  // Fetch audio tracks when Audio tab is active
  useEffect(() => {
    if (mainMediaTab === 'Audio' && userId) {
      fetchAudioTracks();
      fetchAudioTags();
      fetchPlaylists();
    }
  }, [mainMediaTab, userId]);

  // Fetch photos when Photos tab is active
  useEffect(() => {
    if (mainMediaTab === 'Photos' && userId) {
      fetchPhotos();
    }
  }, [mainMediaTab, userId]);

  const fetchPhotos = async () => {
    if (!userId) return;
    setPhotosLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/photo-storage/photos?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setPhotos(data.photos || []);
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
    } finally {
      setPhotosLoading(false);
    }
  };

  // Fetch photo categories from database
  const fetchPhotoCategories = async () => {
    if (!userId) return;
    setPhotoCategoriesLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/photo-storage/categories?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setPhotoCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching photo categories:', error);
    } finally {
      setPhotoCategoriesLoading(false);
    }
  };

  // Fetch categories when Photos tab is active
  useEffect(() => {
    if (mainMediaTab === 'Photos' && userId) {
      fetchPhotoCategories();
    }
  }, [mainMediaTab, userId]);

  // Add new category
  const handleAddPhotoCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const response = await fetch(`${API_URL}/api/photo-storage/categories?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newCategoryName.trim() })
      });
      if (response.ok) {
        const data = await response.json();
        setPhotoCategories(prev => [...prev, data.category]);
        setNewCategoryName('');
        toast.success('Category added!');
      }
    } catch (error) {
      toast.error('Failed to add category');
    }
  };

  // Update category name
  const handleUpdatePhotoCategory = async (categoryId, newName) => {
    try {
      const response = await fetch(`${API_URL}/api/photo-storage/categories/${categoryId}?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName })
      });
      if (response.ok) {
        setPhotoCategories(prev => prev.map(c => c.id === categoryId ? { ...c, name: newName } : c));
        setEditingCategoryId(null);
        toast.success('Category updated!');
      }
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  // Delete category
  const handleDeletePhotoCategory = async (categoryId) => {
    if (photoCategories.length <= 1) {
      toast.error('Cannot delete the last category');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/photo-storage/categories/${categoryId}?user_id=${userId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setPhotoCategories(prev => prev.filter(c => c.id !== categoryId));
        toast.success('Category deleted!');
      }
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Reorder categories
  const handleReorderPhotoCategories = async (dragIndex, dropIndex) => {
    const newCategories = [...photoCategories];
    const [dragged] = newCategories.splice(dragIndex, 1);
    newCategories.splice(dropIndex, 0, dragged);
    setPhotoCategories(newCategories);
    
    try {
      await fetch(`${API_URL}/api/photo-storage/categories/reorder?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category_ids: newCategories.map(c => c.id) })
      });
    } catch (error) {
      console.error('Failed to save category order');
      fetchPhotoCategories(); // Revert on error
    }
  };

  // Open photo edit modal
  const openEditPhotoModal = (photo) => {
    setEditingPhoto(photo);
    setEditPhotoTitle(photo.title || '');
    setEditPhotoCaption(photo.caption || '');
    setEditPhotoTags(photo.tags?.join(', ') || '');
    setEditPhotoCategory(photo.category || 'General');
    setShowPhotoEditModal(true);
  };

  // Update photo
  const handleUpdatePhoto = async () => {
    if (!editingPhoto) return;
    setIsUpdatingPhoto(true);
    try {
      const response = await fetch(`${API_URL}/api/photo-storage/photos/${editingPhoto.id}?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editPhotoTitle,
          caption: editPhotoCaption,
          tags: editPhotoTags ? editPhotoTags.split(',').map(t => t.trim()).filter(Boolean) : [],
          category: editPhotoCategory
        })
      });
      if (response.ok) {
        setPhotos(prev => prev.map(p => 
          p.id === editingPhoto.id 
            ? { ...p, title: editPhotoTitle, caption: editPhotoCaption, tags: editPhotoTags.split(',').map(t => t.trim()).filter(Boolean), category: editPhotoCategory }
            : p
        ));
        setShowPhotoEditModal(false);
        setEditingPhoto(null);
        toast.success('Photo updated!');
      } else {
        toast.error('Failed to update photo');
      }
    } catch (error) {
      toast.error('Failed to update photo');
    } finally {
      setIsUpdatingPhoto(false);
    }
  };

  // Delete photo
  const handleDeletePhoto = async (photoId) => {
    try {
      const response = await fetch(`${API_URL}/api/photo-storage/photos/${photoId}?user_id=${userId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setPhotos(prev => prev.filter(p => p.id !== photoId));
        setShowPhotoEditModal(false);
        setEditingPhoto(null);
        toast.success('Photo deleted!');
      }
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleUploadPhotos = async () => {
    if (selectedPhotoFiles.length === 0) {
      toast.error('Please select photos to upload');
      return;
    }

    setIsUploadingPhotos(true);
    try {
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('category', photoCategory);
      if (photoAlbum) formData.append('album', photoAlbum);
      
      // For multiple photos
      selectedPhotoFiles.forEach(file => {
        formData.append('photos', file);
      });

      const response = await fetch(`${API_URL}/api/photo-storage/upload-multiple`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setPhotos(prev => [...data.photos, ...prev]);
        resetPhotoUploadModal();
        toast.success(`Uploaded ${data.uploaded} photo(s) successfully!`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error('Failed to upload photos');
    } finally {
      setIsUploadingPhotos(false);
    }
  };

  const resetPhotoUploadModal = () => {
    setShowPhotoUploadModal(false);
    setSelectedPhotoFiles([]);
    setPhotoTitle('');
    setPhotoCaption('');
    setPhotoTags('');
    setPhotoCategory('General');
    setPhotoAlbum('');
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const filteredPhotos = photos.filter(photo => {
    // Filter by category
    if (activePhotoCategoryFilter !== 'All' && photo.category !== activePhotoCategoryFilter) {
      return false;
    }
    // Filter by search query
    if (photoSearchQuery) {
      const query = photoSearchQuery.toLowerCase();
      const matchesTitle = photo.title?.toLowerCase().includes(query);
      const matchesCaption = photo.caption?.toLowerCase().includes(query);
      const matchesTags = photo.tags?.some(tag => tag.toLowerCase().includes(query));
      if (!matchesTitle && !matchesCaption && !matchesTags) return false;
    }
    return true;
  });

  const fetchAudioTracks = async () => {
    if (!userId) return;
    setAudioLoading(true);
    try {
      let url = `${API_URL}/api/audio-storage/tracks?user_id=${userId}`;
      if (audioTagFilter) {
        url += `&tag=${encodeURIComponent(audioTagFilter)}`;
      }
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setAudioTracks(data.tracks || []);
      }
    } catch (error) {
      console.error('Error fetching audio tracks:', error);
    } finally {
      setAudioLoading(false);
    }
  };

  const fetchAudioTags = async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_URL}/api/audio-storage/tags?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setAllAudioTags(data.tags || []);
      }
    } catch (error) {
      console.error('Error fetching audio tags:', error);
    }
  };

  const playTrack = (track) => {
    // Use global audio player context
    globalPlayTrack(track, filteredAudioTracks);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleUploadAudio = async () => {
    if (!selectedAudioFile || !audioTitle) {
      toast.error('Please select an audio file and enter a title');
      return;
    }

    setIsUploadingAudio(true);
    try {
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('title', audioTitle);
      formData.append('artist', audioArtist || 'Unknown Artist');
      if (audioBpm) formData.append('bpm', audioBpm);
      if (audioKey) formData.append('key', audioKey);
      formData.append('tags', audioTags);
      if (audioPrice) formData.append('price', audioPrice);
      formData.append('is_free', audioIsFree);
      formData.append('category', audioCategory);
      if (audioGenre) formData.append('genre', audioGenre);
      if (audioMood) formData.append('mood', audioMood);
      if (audioInstrument) formData.append('instrument', audioInstrument);
      if (audioTrackType) formData.append('track_type', audioTrackType);
      if (audioPublishedDate) formData.append('published_date', audioPublishedDate);
      formData.append('audio_file', selectedAudioFile);
      if (selectedCoverFile) {
        formData.append('cover_file', selectedCoverFile);
      }

      const response = await fetch(`${API_URL}/api/audio-storage/upload`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setAudioTracks(prev => [data.track, ...prev]);
        resetAudioUploadModal();
        toast.success('Track uploaded successfully!');
        fetchAudioTags(); // Refresh tags
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Audio upload error:', error);
      toast.error('Failed to upload track');
    } finally {
      setIsUploadingAudio(false);
    }
  };

  const resetAudioUploadModal = () => {
    setShowAudioUploadModal(false);
    setAudioTitle('');
    setAudioArtist('');
    setAudioBpm('');
    setAudioKey('');
    setAudioTags('');
    setAudioPrice('');
    setAudioIsFree(true);
    setAudioCategory('Beats');
    setAudioGenre('');
    setAudioMood('');
    setAudioInstrument('');
    setAudioTrackType('');
    setAudioPublishedDate('');
    setSelectedAudioFile(null);
    setSelectedCoverFile(null);
    setDetectingUploadBpm(false);
    setGeneratingUploadCover(false);
    setGeneratedUploadCoverPreview(null);
    if (audioInputRef.current) audioInputRef.current.value = '';
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  const handleDeleteTrack = async (trackId) => {
    if (!window.confirm('Are you sure you want to delete this track?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/audio-storage/tracks/${trackId}?user_id=${userId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setAudioTracks(prev => prev.filter(t => t.id !== trackId));
        if (currentlyPlaying?.id === trackId) {
          stopPlayback();
        }
        toast.success('Track deleted');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete track');
    }
  };

  // Bulk selection helpers
  const toggleTrackSelection = (e, trackId) => {
    e.stopPropagation();
    setSelectedTrackIds(prev => {
      const next = new Set(prev);
      next.has(trackId) ? next.delete(trackId) : next.add(trackId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const visibleIds = filteredAudioTracks
      .slice((audioPage - 1) * TRACKS_PER_PAGE, audioPage * TRACKS_PER_PAGE)
      .map(t => t.id);
    const allSelected = visibleIds.every(id => selectedTrackIds.has(id));
    setSelectedTrackIds(prev => {
      const next = new Set(prev);
      visibleIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const handleBulkUpload = async () => {
    if (bulkUploadFiles.length === 0) return;
    setIsBulkUploading(true);
    const files = [...bulkUploadFiles];
    let successCount = 0;
    for (let i = 0; i < files.length; i++) {
      if (files[i].status === 'done') continue;
      files[i] = { ...files[i], status: 'uploading' };
      setBulkUploadFiles([...files]);
      try {
        const formData = new FormData();
        formData.append('user_id', userId);
        formData.append('title', files[i].file.name.replace(/\.[^/.]+$/, ''));
        formData.append('artist', bulkArtist || 'Unknown Artist');
        if (bulkGenre) formData.append('genre', bulkGenre);
        if (bulkMood) formData.append('mood', bulkMood);
        formData.append('category', bulkCategory || 'Beats');
        formData.append('is_free', bulkIsFree);
        if (!bulkIsFree && bulkPrice) formData.append('price', bulkPrice);
        formData.append('audio_file', files[i].file);
        const res = await fetch(`${API_URL}/api/audio-storage/upload`, { method: 'POST', body: formData });
        if (res.ok) {
          const data = await res.json();
          setAudioTracks(prev => [data.track, ...prev]);
          files[i] = { ...files[i], status: 'done' };
          successCount++;
        } else {
          const err = await res.json();
          files[i] = { ...files[i], status: 'error', error: err.detail || 'Failed' };
        }
      } catch (error) {
        files[i] = { ...files[i], status: 'error', error: error.message };
      }
      setBulkUploadFiles([...files]);
    }
    setIsBulkUploading(false);
    if (successCount > 0) {
      toast.success(`${successCount} track${successCount > 1 ? 's' : ''} uploaded!`);
      fetchAudioTags();
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedTrackIds.size;
    if (!window.confirm(`Delete ${count} selected track${count > 1 ? 's' : ''}? This cannot be undone.`)) return;
    setIsBulkDeleting(true);
    try {
      const ids = Array.from(selectedTrackIds);
      const res = await fetch(`${API_URL}/api/audio-storage/tracks/bulk?user_id=${userId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ track_ids: ids }),
      });
      if (res.ok) {
        setAudioTracks(prev => prev.filter(t => !selectedTrackIds.has(t.id)));
        setSelectedTrackIds(new Set());
        toast.success(`${ids.length} track${ids.length > 1 ? 's' : ''} deleted`);
      } else {
        toast.error('Bulk delete failed');
      }
    } catch (error) {
      toast.error('Bulk delete failed');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleBulkEdit = async () => {
    const updates = {};
    if (bulkEditArtist) updates.artist = bulkEditArtist;
    if (bulkEditGenre) updates.genre = bulkEditGenre;
    if (bulkEditMood) updates.mood = bulkEditMood;
    if (bulkEditCategory) updates.category = bulkEditCategory;
    if (bulkEditPrice !== '') updates.price = parseFloat(bulkEditPrice) || 0;
    if (bulkEditIsFree !== '') updates.is_free = bulkEditIsFree === 'true';
    if (bulkEditTags) updates.tags = bulkEditTags.split(',').map(t => t.trim()).filter(Boolean);
    if (Object.keys(updates).length === 0) { toast.error('Fill in at least one field to update'); return; }
    setIsBulkEditing(true);
    try {
      const ids = Array.from(selectedTrackIds);
      const res = await fetch(`${API_URL}/api/audio-storage/tracks/bulk-edit?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ track_ids: ids, updates }),
      });
      if (res.ok) {
        setAudioTracks(prev => prev.map(t => selectedTrackIds.has(t.id) ? { ...t, ...updates } : t));
        setSelectedTrackIds(new Set());
        setShowBulkEditModal(false);
        setBulkEditArtist(''); setBulkEditGenre(''); setBulkEditMood(''); setBulkEditCategory('');
        setBulkEditIsFree(''); setBulkEditPrice(''); setBulkEditTags('');
        toast.success(`Updated ${ids.length} track${ids.length > 1 ? 's' : ''}`);
      } else {
        toast.error('Bulk edit failed');
      }
    } catch (error) {
      toast.error('Bulk edit failed');
    } finally {
      setIsBulkEditing(false);
    }
  };

  // Playlist functions
  const fetchPlaylists = async () => {
    if (!userId) return;
    setPlaylistsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/playlists?user_id=${userId}`);
      if (res.ok) { const data = await res.json(); setPlaylists(data.playlists || []); }
    } catch { /* silent */ } finally { setPlaylistsLoading(false); }
  };

  const openCreatePlaylist = () => {
    setPlaylistName(''); setPlaylistDescription(''); setPlaylistSelectedTracks([]);
    setPlaylistTrackSearch(''); setShowCreatePlaylistModal(true);
  };

  const openEditPlaylist = (pl) => {
    setEditingPlaylist(pl);
    setPlaylistName(pl.name);
    setPlaylistDescription(pl.description || '');
    setPlaylistSelectedTracks(pl.track_ids || []);
    setPlaylistTrackSearch('');
    setShowEditPlaylistModal(true);
  };

  const handleSavePlaylist = async (isEdit = false) => {
    if (!playlistName.trim()) { toast.error('Playlist name is required'); return; }
    setIsSavingPlaylist(true);
    try {
      if (isEdit && editingPlaylist) {
        const res = await fetch(`${API_URL}/api/playlists/${editingPlaylist.id}?user_id=${userId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: playlistName, description: playlistDescription, track_ids: playlistSelectedTracks }),
        });
        if (res.ok) {
          setPlaylists(prev => prev.map(p => p.id === editingPlaylist.id
            ? { ...p, name: playlistName, description: playlistDescription, track_ids: playlistSelectedTracks, track_count: playlistSelectedTracks.length }
            : p));
          setShowEditPlaylistModal(false);
          toast.success('Playlist updated!');
        } else toast.error('Failed to update playlist');
      } else {
        const res = await fetch(`${API_URL}/api/playlists`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, name: playlistName, description: playlistDescription, track_ids: playlistSelectedTracks }),
        });
        if (res.ok) {
          const data = await res.json();
          setPlaylists(prev => [{ ...data.playlist, track_count: playlistSelectedTracks.length }, ...prev]);
          setShowCreatePlaylistModal(false);
          toast.success('Playlist created!');
        } else toast.error('Failed to create playlist');
      }
    } catch { toast.error('Save failed'); } finally { setIsSavingPlaylist(false); }
  };

  const handleDeletePlaylist = async (playlistId) => {
    if (!window.confirm('Delete this playlist?')) return;
    try {
      const res = await fetch(`${API_URL}/api/playlists/${playlistId}?user_id=${userId}`, { method: 'DELETE' });
      if (res.ok) { setPlaylists(prev => prev.filter(p => p.id !== playlistId)); toast.success('Playlist deleted'); }
      else toast.error('Failed to delete playlist');
    } catch { toast.error('Delete failed'); }
  };

  const copyPlaylistLink = (playlistId) => {
    navigator.clipboard.writeText(`${window.location.origin}/playlist/${playlistId}`)
      .catch(err => console.error('Clipboard write failed:', err));
    setPlaylistLinkCopied(playlistId);
    toast.success('Playlist link copied!');
    setTimeout(() => setPlaylistLinkCopied(null), 2000);
  };

  const togglePlaylistTrack = (trackId) => {
    setPlaylistSelectedTracks(prev =>
      prev.includes(trackId) ? prev.filter(id => id !== trackId) : [...prev, trackId]
    );
  };

  // Open edit modal with track data
  const openEditTrackModal = (track) => {
    setEditingTrack(track);
    setEditTrackTitle(track.title || '');
    setEditTrackArtist(track.artist || '');
    setEditTrackBpm(track.bpm?.toString() || '');
    setEditTrackKey(track.key || '');
    setEditTrackTags(track.tags?.join(', ') || '');
    setEditTrackPrice(track.price?.toString() || '');
    setEditTrackIsFree(track.is_free !== false);
    setEditTrackCategory(track.category || 'Beats');
    setEditTrackGenre(track.genre || '');
    setEditTrackMood(track.mood || '');
    setEditTrackInstrument(track.instrument || '');
    setEditTrackType(track.track_type || '');
    setEditTrackPublishedDate(track.published_date || '');
    setEditCoverFile(null);
    setEditCoverPreview(null);
    setEditAudioFile(null);
    setEditAudioFileName(null);
    setShowAudioEditModal(true);
  };

  // Handle edit cover file selection
  const handleEditCoverSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditCoverFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setEditCoverPreview(previewUrl);
    }
  };

  // Handle audio file replacement selection
  const handleEditAudioSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('audio/')) {
        toast.error('Please select a valid audio file');
        return;
      }
      setEditAudioFile(file);
      setEditAudioFileName(file.name);
      toast.success(`Selected: ${file.name}`);
    }
  };

  // Generate AI cover art for upload modal
  const generateUploadCover = async () => {
    if (!audioTitle) {
      toast.error('Please enter a track title first');
      return;
    }
    
    setGeneratingUploadCover(true);
    const loadingToast = toast.loading('Generating AI cover art with multiple styles...');
    
    try {
      const response = await fetch(`${API_URL}/api/ai-cover/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: audioTitle,
          artist: audioArtist,
          genre: audioGenre,
          mood: audioMood,
          bpm: audioBpm ? parseInt(audioBpm) : null,
          key: audioKey,
          tags: audioTags ? audioTags.split(',').map(t => t.trim()) : [],
          provider: 'random'  // Randomly select between Gemini and OpenAI for variety
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success && data.image_base64) {
        // Create a preview URL from base64
        const previewUrl = `data:${data.mime_type};base64,${data.image_base64}`;
        setGeneratedUploadCoverPreview(previewUrl);
        
        // Convert base64 to File for upload
        const byteCharacters = atob(data.image_base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.mime_type });
        const file = new File([blob], 'ai_cover.png', { type: data.mime_type });
        setSelectedCoverFile(file);
        
        const providerName = data.provider === 'openai' ? 'OpenAI GPT Image' : 'Gemini';
        toast.success(`Cover art generated with ${providerName}!`, { id: loadingToast });
      } else {
        throw new Error(data.detail || data.error || 'Generation failed');
      }
    } catch (error) {
      console.error('AI cover generation error:', error);
      toast.error(`Failed to generate cover: ${error.message}`, { id: loadingToast });
    } finally {
      setGeneratingUploadCover(false);
    }
  };

  // Generate AI cover art for edit modal (and save directly)
  const generateEditCover = async () => {
    if (!editingTrack) return;
    
    setGeneratingEditCover(true);
    const loadingToast = toast.loading('Generating AI cover art with multiple styles...');
    
    try {
      const response = await fetch(`${API_URL}/api/ai-cover/generate-and-save/${editingTrack.id}?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editTrackTitle || editingTrack.title,
          artist: editTrackArtist || editingTrack.artist,
          genre: editTrackGenre || editingTrack.genre,
          mood: editTrackMood || editingTrack.mood,
          bpm: editTrackBpm ? parseInt(editTrackBpm) : editingTrack.bpm,
          key: editTrackKey || editingTrack.key,
          tags: editTrackTags ? editTrackTags.split(',').map(t => t.trim()) : editingTrack.tags,
          provider: 'random'  // Randomly select between Gemini and OpenAI for variety
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success && data.cover_url) {
        // Update local state with new cover
        setAudioTracks(prev => prev.map(t => 
          t.id === editingTrack.id ? { ...t, cover_url: data.cover_url } : t
        ));
        setEditingTrack(prev => ({ ...prev, cover_url: data.cover_url }));
        setEditCoverFile(null);
        setEditCoverPreview(null);
        const providerName = data.provider === 'openai' ? 'OpenAI GPT Image' : 'Gemini';
        toast.success(`Cover art generated with ${providerName} and saved!`, { id: loadingToast });
      } else {
        throw new Error(data.detail || data.error || 'Generation failed');
      }
    } catch (error) {
      console.error('AI cover generation error:', error);
      toast.error(`Failed to generate cover: ${error.message}`, { id: loadingToast });
    } finally {
      setGeneratingEditCover(false);
    }
  };

  // Separate BPM Detection for Edit Modal
  const detectEditBpm = async (audioUrl) => {
    setDetectingBpm(true);
    const loadingToast = toast.loading('Detecting BPM...');
    
    try {
      const response = await fetch(`${API_URL}${audioUrl}`);
      const blob = await response.blob();
      const file = new File([blob], 'audio.mp3', { type: blob.type });
      
      const result = await detectBPMOnly(file);
      
      if (result.success && result.bpm) {
        setEditTrackBpm(result.bpm.toString());
        toast.success(`BPM: ${result.bpm}`, { id: loadingToast });
      } else {
        toast.error('Could not detect BPM. Please enter manually.', { id: loadingToast });
      }
    } catch (error) {
      console.error('BPM detection error:', error);
      toast.error('BPM detection failed.', { id: loadingToast });
    } finally {
      setDetectingBpm(false);
    }
  };

  // Separate Key Detection for Edit Modal - Uses server-side librosa analysis for accuracy
  const detectEditKey = async (audioUrl) => {
    setDetectingKey(true);
    const loadingToast = toast.loading('Analyzing Key with librosa...');
    
    try {
      // Extract file_id from audio URL (format: /api/audio-storage/stream/{file_id})
      const fileIdMatch = audioUrl.match(/\/stream\/([a-f0-9]+)/);
      
      let result;
      if (fileIdMatch) {
        // Use the fast GridFS-based endpoint
        const fileId = fileIdMatch[1];
        const response = await fetch(`${API_URL}/api/audio/analyze-key-by-id/${fileId}`, {
          method: 'POST'
        });
        
        if (!response.ok) {
          throw new Error('Server analysis failed');
        }
        result = await response.json();
      } else {
        // Fallback: Download and re-upload for external URLs
        const response = await fetch(`${API_URL}${audioUrl}`);
        const blob = await response.blob();
        
        const ext = audioUrl.split('.').pop()?.split('?')[0] || 'mp3';
        const file = new File([blob], `audio.${ext}`, { type: blob.type || 'audio/mpeg' });
        
        const formData = new FormData();
        formData.append('file', file);
        
        const analysisResponse = await fetch(`${API_URL}/api/audio/analyze-key`, {
          method: 'POST',
          body: formData
        });
        
        if (!analysisResponse.ok) {
          throw new Error('Server analysis failed');
        }
        result = await analysisResponse.json();
      }
      
      if (result.success && result.full_key) {
        setEditTrackKey(result.full_key);
        const bpmInfo = result.bpm ? ` | BPM: ${result.bpm}` : '';
        const confidenceInfo = result.confidence ? ` (${result.confidence}% confidence)` : '';
        toast.success(`Key: ${result.full_key}${confidenceInfo}${bpmInfo}`, { id: loadingToast });
      } else {
        toast.error(result.error || 'Could not detect Key. Please enter manually.', { id: loadingToast });
      }
    } catch (error) {
      console.error('Key detection error:', error);
      toast.error('Key detection failed.', { id: loadingToast });
    } finally {
      setDetectingKey(false);
    }
  };

  // Separate BPM Detection for Upload Modal
  const detectUploadBpm = async (file) => {
    if (!file) {
      toast.error('Please select an audio file first');
      return;
    }
    setDetectingUploadBpm(true);
    const loadingToast = toast.loading('Detecting BPM...');
    
    try {
      const result = await detectBPMOnly(file);
      
      if (result.success && result.bpm) {
        setAudioBpm(result.bpm.toString());
        toast.success(`BPM: ${result.bpm}`, { id: loadingToast });
      } else {
        toast.error('Could not detect BPM. Please enter manually.', { id: loadingToast });
      }
    } catch (error) {
      console.error('BPM detection error:', error);
      toast.error('BPM detection failed.', { id: loadingToast });
    } finally {
      setDetectingUploadBpm(false);
    }
  };

  // Separate Key Detection for Upload Modal - Uses server-side librosa analysis for accuracy
  const detectUploadKey = async (file) => {
    if (!file) {
      toast.error('Please select an audio file first');
      return;
    }
    setDetectingUploadKey(true);
    const loadingToast = toast.loading('Analyzing Key with librosa...');
    
    try {
      // Send to server-side librosa analysis endpoint
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/audio/analyze-key`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Server analysis failed');
      }
      
      const result = await response.json();
      
      if (result.success && result.full_key) {
        setAudioKey(result.full_key);
        const bpmInfo = result.bpm ? ` | BPM: ${result.bpm}` : '';
        const confidenceInfo = result.confidence ? ` (${result.confidence}% confidence)` : '';
        toast.success(`Key: ${result.full_key}${confidenceInfo}${bpmInfo}`, { id: loadingToast });
      } else {
        toast.error(result.error || 'Could not detect Key. Please enter manually.', { id: loadingToast });
      }
    } catch (error) {
      console.error('Key detection error:', error);
      toast.error('Key detection failed.', { id: loadingToast });
    } finally {
      setDetectingUploadKey(false);
    }
  };

  // Update track function
  const handleUpdateTrack = async () => {
    if (!editingTrack) return;
    
    setIsUpdatingTrack(true);
    try {
      // First, upload new audio file if selected
      let newAudioUrl = editingTrack.audio_url;
      let newDuration = editingTrack.duration;
      
      if (editAudioFile) {
        setUploadingEditAudio(true);
        const audioFormData = new FormData();
        audioFormData.append('audio_file', editAudioFile);
        
        try {
          const audioResponse = await fetch(`${API_URL}/api/audio-storage/tracks/${editingTrack.id}/audio?user_id=${userId}`, {
            method: 'PUT',
            body: audioFormData
          });
          
          if (audioResponse.ok) {
            const audioResult = await audioResponse.json();
            newAudioUrl = audioResult.audio_url;
            newDuration = audioResult.duration || editingTrack.duration;
            toast.success('Audio file replaced successfully');
          } else {
            const errorData = await audioResponse.json().catch(() => ({}));
            toast.error(errorData.detail || 'Failed to replace audio file');
            setUploadingEditAudio(false);
            setIsUpdatingTrack(false);
            return;
          }
        } catch (audioError) {
          console.error('Audio upload error:', audioError);
          toast.error('Failed to replace audio file');
          setUploadingEditAudio(false);
          setIsUpdatingTrack(false);
          return;
        }
        setUploadingEditAudio(false);
      }

      // Then, upload new cover if a file was manually selected (not AI generated)
      let newCoverUrl = editingTrack.cover_url;
      if (editCoverFile) {
        const coverFormData = new FormData();
        coverFormData.append('cover_file', editCoverFile);
        
        try {
          const coverResponse = await fetch(`${API_URL}/api/audio-storage/tracks/${editingTrack.id}/cover?user_id=${userId}`, {
            method: 'PUT',
            body: coverFormData
          });
          
          if (coverResponse.ok) {
            const coverResult = await coverResponse.json();
            newCoverUrl = coverResult.cover_url;
          } else {
            console.warn('Cover upload failed, continuing with metadata update');
          }
        } catch (coverError) {
          console.warn('Cover upload error:', coverError);
          // Continue with metadata update even if cover upload fails
        }
      }

      // Then update the track metadata
      const updateData = {
        title: editTrackTitle,
        artist: editTrackArtist,
        bpm: editTrackBpm ? parseInt(editTrackBpm) : null,
        key: editTrackKey || null,
        tags: editTrackTags ? editTrackTags.split(',').map(t => t.trim()).filter(Boolean) : [],
        price: editTrackPrice ? parseFloat(editTrackPrice) : null,
        is_free: editTrackIsFree,
        category: editTrackCategory,
        genre: editTrackGenre || null,
        mood: editTrackMood || null,
        instrument: editTrackInstrument || null,
        track_type: editTrackType || null,
        published_date: editTrackPublishedDate || null
      };

      const response = await fetch(`${API_URL}/api/audio-storage/tracks/${editingTrack.id}?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        // Update local state with new cover URL and audio URL
        setAudioTracks(prev => prev.map(t => 
          t.id === editingTrack.id ? { ...t, ...updateData, cover_url: newCoverUrl, audio_url: newAudioUrl, duration: newDuration } : t
        ));
        setShowAudioEditModal(false);
        setEditingTrack(null);
        setEditCoverFile(null);
        setEditCoverPreview(null);
        setEditAudioFile(null);
        setEditAudioFileName(null);
        toast.success('Track updated successfully!');
        fetchAudioTags(); // Refresh tags
      } else {
        // Try to get error details
        let errorMessage = 'Failed to update track';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // Response might not be JSON
        }
        console.error('Update failed:', response.status, errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update track: ' + (error.message || 'Unknown error'));
    } finally {
      setIsUpdatingTrack(false);
    }
  };

  // Track drag and drop handlers
  const handleTrackDragStart = (e, index) => {
    setDraggedTrackIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleTrackDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedTrackIndex(null);
  };

  const handleTrackDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleTrackDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedTrackIndex === null || draggedTrackIndex === dropIndex) return;
    
    // Get the actual track IDs from the filtered/paginated view
    const displayedTracks = filteredAudioTracks.slice(
      (audioPage - 1) * TRACKS_PER_PAGE, 
      audioPage * TRACKS_PER_PAGE
    );
    
    const draggedTrackId = displayedTracks[draggedTrackIndex]?.id;
    const dropTrackId = displayedTracks[dropIndex]?.id;
    
    if (!draggedTrackId || !dropTrackId) {
      setDraggedTrackIndex(null);
      return;
    }
    
    // Find actual indices in the main audioTracks array
    const actualDragIndex = audioTracks.findIndex(t => t.id === draggedTrackId);
    const actualDropIndex = audioTracks.findIndex(t => t.id === dropTrackId);
    
    if (actualDragIndex === -1 || actualDropIndex === -1) {
      setDraggedTrackIndex(null);
      return;
    }
    
    // Reorder the main audioTracks array
    const newTracks = [...audioTracks];
    const [draggedTrack] = newTracks.splice(actualDragIndex, 1);
    newTracks.splice(actualDropIndex, 0, draggedTrack);
    setAudioTracks(newTracks);
    
    // Save new order to backend - send ALL tracks with their new order
    try {
      const trackOrder = newTracks.map((track, idx) => ({ id: track.id, order: idx }));
      console.log('Saving track order:', trackOrder);
      
      const response = await fetch(`${API_URL}/api/audio-storage/tracks/reorder?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ track_order: trackOrder })
      });
      
      if (response.ok) {
        toast.success('Track order saved permanently');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Reorder failed:', errorData);
        throw new Error('Failed to save order');
      }
    } catch (error) {
      console.error('Failed to save track order:', error);
      toast.error('Failed to save track order');
      // Revert on error
      fetchAudioTracks();
    }
    
    setDraggedTrackIndex(null);
  };

  const filteredAudioTracks = audioTracks.filter(track => {
    // Search query filter
    if (audioSearchQuery) {
      const query = audioSearchQuery.toLowerCase();
      const matchesTitle = track.title?.toLowerCase().includes(query);
      const matchesArtist = track.artist?.toLowerCase().includes(query);
      const matchesTags = track.tags?.some(tag => tag.toLowerCase().includes(query));
      if (!matchesTitle && !matchesArtist && !matchesTags) return false;
    }
    
    // Tag filter (from tag pills)
    if (audioTagFilter && track.tags) {
      if (!track.tags.some(tag => tag.toLowerCase() === audioTagFilter.toLowerCase())) {
        return false;
      }
    }
    
    // Genre filter
    if (audioFilters.genre && track.genre !== audioFilters.genre) {
      return false;
    }
    
    // Track Type / Category filter
    if (audioFilters.trackType && track.category !== audioFilters.trackType) {
      return false;
    }
    
    // Mood filter
    if (audioFilters.mood && track.mood !== audioFilters.mood) {
      return false;
    }
    
    // Key filter
    if (audioFilters.key) {
      const trackKey = track.key ? `${track.key}` : '';
      if (!trackKey.toLowerCase().includes(audioFilters.key.toLowerCase())) {
        return false;
      }
    }
    
    // Instrument filter
    if (audioFilters.instrument && track.instrument !== audioFilters.instrument) {
      return false;
    }
    
    // BPM filter
    if (audioFilters.bpm && track.bpm) {
      const bpm = parseInt(track.bpm);
      const range = audioFilters.bpm;
      if (range === '60-80 BPM' && (bpm < 60 || bpm > 80)) return false;
      if (range === '80-100 BPM' && (bpm < 80 || bpm > 100)) return false;
      if (range === '100-120 BPM' && (bpm < 100 || bpm > 120)) return false;
      if (range === '120-140 BPM' && (bpm < 120 || bpm > 140)) return false;
      if (range === '140-160 BPM' && (bpm < 140 || bpm > 160)) return false;
      if (range === '160+ BPM' && bpm < 160) return false;
    }
    
    // Duration filter
    if (audioFilters.duration && track.duration) {
      const duration = parseInt(track.duration); // in seconds
      const range = audioFilters.duration;
      if (range === 'Under 1 min' && duration >= 60) return false;
      if (range === '1-2 min' && (duration < 60 || duration >= 120)) return false;
      if (range === '2-3 min' && (duration < 120 || duration >= 180)) return false;
      if (range === '3-5 min' && (duration < 180 || duration >= 300)) return false;
      if (range === 'Over 5 min' && duration < 300) return false;
    }
    
    // Price filter
    if (audioFilters.price) {
      const price = track.price || 0;
      const isFree = track.is_free || price === 0;
      const range = audioFilters.price;
      if (range === 'Free' && !isFree) return false;
      if (range === 'Under $25' && (isFree || price >= 25)) return false;
      if (range === '$25-$50' && (isFree || price < 25 || price > 50)) return false;
      if (range === '$50-$100' && (price < 50 || price > 100)) return false;
      if (range === 'Over $100' && price <= 100) return false;
    }
    
    return true;
  }).sort((a, b) => {
    if (audioSortBy === 'date_newest') {
      const dateA = a.published_date ? new Date(a.published_date + 'T12:00:00') : new Date(0);
      const dateB = b.published_date ? new Date(b.published_date + 'T12:00:00') : new Date(0);
      return dateB - dateA;
    }
    if (audioSortBy === 'date_oldest') {
      const dateA = a.published_date ? new Date(a.published_date + 'T12:00:00') : new Date(0);
      const dateB = b.published_date ? new Date(b.published_date + 'T12:00:00') : new Date(0);
      return dateA - dateB;
    }
    return 0; // keep default order
  });
  
  // Count active filters for badge
  const activeFilterCount = Object.values(audioFilters).filter(v => v).length + (audioTagFilter ? 1 : 0) + (audioSortBy ? 1 : 0);
  
  // Reset pagination when filters change
  useEffect(() => {
    setAudioPage(1);
  }, [audioFilters, audioTagFilter, audioSearchQuery, audioSortBy]);

  const fetchVideos = async () => {
    if (!userId) {
      setIsLoading(false);
      setStoryVideos([]);
      setLibraryVideos([]);
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/media-storage/videos?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        const videos = data.videos || [];

        // Preload removed — VideoThumbnail uses IntersectionObserver for lazy loading
        setStoryVideos(videos.filter(v => v.video_type === 'story' || v.type === 'story'));
        setLibraryVideos(videos.filter(v => v.video_type === 'library' || v.type === 'library'));
      } else {
        setStoryVideos([]);
        setLibraryVideos([]);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      setStoryVideos([]);
      setLibraryVideos([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter videos by active category tab
  const filteredStoryVideos = activeTab === 'All' 
    ? storyVideos 
    : storyVideos.filter(v => v.category === activeTab);
  
  const filteredLibraryVideos = activeTab === 'All' 
    ? libraryVideos 
    : libraryVideos.filter(v => v.category === activeTab);

  // Generate thumbnail from video file
  const generateThumbnailFromVideo = useCallback((file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      
      video.onloadeddata = () => {
        video.currentTime = Math.min(1, video.duration / 4); // Capture frame at 1s or 25% of video
      };
      
      video.onseeked = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          resolve(blob);
          URL.revokeObjectURL(video.src);
        }, 'image/jpeg', 0.8);
      };
      
      video.onerror = () => {
        resolve(null);
        URL.revokeObjectURL(video.src);
      };
      
      video.src = URL.createObjectURL(file);
    });
  }, []);

  // Extract YouTube video ID from URL
  const extractYoutubeId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Handle video file selection with auto thumbnail generation
  const handleVideoSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedVideoFile(file);
      if (!videoTitle) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setVideoTitle(nameWithoutExt);
      }
      
      // Auto-generate thumbnail
      const thumbnailBlob = await generateThumbnailFromVideo(file);
      if (thumbnailBlob) {
        setGeneratedThumbnail(thumbnailBlob);
        setSelectedThumbnailFile(null); // Clear custom thumbnail if auto-generated
      }
    }
  };

  // Handle custom thumbnail selection
  const handleThumbnailSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedThumbnailFile(file);
      setGeneratedThumbnail(null); // Clear auto-generated thumbnail
    }
  };

  // Handle video upload (file or YouTube)
  const handleUploadVideo = async () => {
    if (uploadSource === 'youtube') {
      // YouTube URL upload
      if (!youtubeUrl || !videoTitle) {
        alert('Please enter a YouTube URL and video title');
        return;
      }
      
      const ytId = extractYoutubeId(youtubeUrl);
      if (!ytId) {
        alert('Invalid YouTube URL. Please enter a valid YouTube video link.');
        return;
      }

      setIsUploading(true);
      try {
        const formData = new FormData();
        formData.append('youtube_url', youtubeUrl);
        formData.append('name', videoTitle);
        formData.append('user_id', userId);  // CRITICAL: Include user_id for data isolation
        formData.append('category', videoCategory);
        formData.append('type', uploadType);

        const response = await fetch(`${API_URL}/api/media-storage/upload-youtube`, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          const uploadedVideo = await response.json();
          const newVideo = {
            id: uploadedVideo.id,
            title: videoTitle,
            name: videoTitle,
            category: videoCategory,
            categoryColor: categoryColors[videoCategory] || 'bg-purple-600',
            thumbnail: uploadedVideo.thumbnail,
            youtube_url: uploadedVideo.youtube_url,
            youtube_id: uploadedVideo.youtube_id,
            is_youtube: true,
            duration: '0:00',
            views: '0',
            likes: '0',
            type: uploadType,
            video_type: uploadType,
            agent: 'You',
            isNew: true
          };
          
          if (uploadType === 'story') {
            setStoryVideos(prev => [newVideo, ...prev]);
          } else {
            setLibraryVideos(prev => [newVideo, ...prev]);
          }
          
          resetUploadModal();
        } else {
          const error = await response.json();
          alert(`Upload failed: ${error.detail || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        alert('Failed to add YouTube video. Please try again.');
      } finally {
        setIsUploading(false);
      }
      return;
    }

    // File upload
    if (!selectedVideoFile || !videoTitle) {
      alert('Please select a video file and enter a title');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedVideoFile);
      formData.append('name', videoTitle);
      formData.append('user_id', userId);  // CRITICAL: Include user_id for data isolation
      formData.append('category', videoCategory);
      formData.append('type', uploadType);
      
      // Add thumbnail (custom or auto-generated)
      const thumbnailToUpload = selectedThumbnailFile || generatedThumbnail;
      if (thumbnailToUpload) {
        const thumbnailFile = thumbnailToUpload instanceof Blob && !(thumbnailToUpload instanceof File)
          ? new File([thumbnailToUpload], 'thumbnail.jpg', { type: 'image/jpeg' })
          : thumbnailToUpload;
        formData.append('thumbnail', thumbnailFile);
      }

      const response = await fetch(`${API_URL}/api/media-storage/upload`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const uploadedVideo = await response.json();
        const newVideo = {
          id: uploadedVideo.id,
          title: videoTitle,
          name: videoTitle,
          category: videoCategory,
          categoryColor: categoryColors[videoCategory] || 'bg-purple-600',
          thumbnail: uploadedVideo.thumbnail,
          thumbnail_data: uploadedVideo.thumbnail_data,  // base64 for instant render
          video_url: uploadedVideo.video_url,
          duration: uploadedVideo.duration || '0:00',
          views: '0',
          likes: '0',
          type: uploadType,
          video_type: uploadType,
          agent: 'You',
          isNew: true
        };
        
        if (uploadType === 'story') {
          setStoryVideos(prev => [newVideo, ...prev]);
        } else {
          setLibraryVideos(prev => [newVideo, ...prev]);
        }
        
        resetUploadModal();
      } else {
        const error = await response.json();
        alert(`Upload failed: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload video. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const resetUploadModal = () => {
    setShowUploadModal(false);
    setSelectedVideoFile(null);
    setSelectedThumbnailFile(null);
    setGeneratedThumbnail(null);
    setVideoTitle('');
    setVideoCategory('Highlights');
    setYoutubeUrl('');
    setUploadSource('file');
    if (videoInputRef.current) videoInputRef.current.value = '';
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
  };

  // Handle video deletion
  const handleDeleteVideo = async (videoId, videoType, e) => {
    e?.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    try {
      const response = await fetch(`${API_URL}/api/media-storage/videos/${videoId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        if (videoType === 'story') {
          setStoryVideos(prev => prev.filter(v => v.id !== videoId));
        } else {
          setLibraryVideos(prev => prev.filter(v => v.id !== videoId));
        }
        if (isModalOpen && selectedVideo?.id === videoId) {
          closeVideoModal();
        }
      }
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  // Open edit modal
  const openEditModal = (video, e) => {
    e?.stopPropagation();
    setEditingVideo(video);
    setEditName(video.name || video.title);
    setEditCategory(video.category);
    setEditType(video.video_type || video.type);
    setEditYoutubeUrl(video.youtube_url || '');
    setNewThumbnailFile(null);
    setNewVideoFile(null);
    setShowEditModal(true);
  };

  // Handle video update
  const handleUpdateVideo = async () => {
    if (!editingVideo) return;
    
    setIsUpdating(true);
    try {
      // Build update payload
      const updatePayload = {
        name: editName,
        category: editCategory,
        video_type: editType
      };
      
      // Add YouTube URL if changed
      if (editYoutubeUrl && editYoutubeUrl !== editingVideo.youtube_url) {
        updatePayload.youtube_url = editYoutubeUrl;
      }
      
      // Update metadata
      const updateResponse = await fetch(`${API_URL}/api/media-storage/videos/${editingVideo.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload)
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to update video metadata');
      }

      // Update thumbnail if provided (only for non-YouTube or custom override)
      if (newThumbnailFile) {
        const thumbFormData = new FormData();
        thumbFormData.append('thumbnail', newThumbnailFile);
        await fetch(`${API_URL}/api/media-storage/videos/${editingVideo.id}/thumbnail`, {
          method: 'POST',
          body: thumbFormData
        });
      }

      // Replace video file if provided (only for file-based videos)
      if (newVideoFile && !editingVideo.is_youtube) {
        const videoFormData = new FormData();
        videoFormData.append('file', newVideoFile);
        await fetch(`${API_URL}/api/media-storage/videos/${editingVideo.id}/replace`, {
          method: 'POST',
          body: videoFormData
        });
      }

      // Refresh videos
      await fetchVideos();
      setShowEditModal(false);
      
      // Update selected video if it's the one being edited
      if (selectedVideo?.id === editingVideo.id) {
        setSelectedVideo(prev => ({
          ...prev,
          name: editName,
          title: editName,
          category: editCategory,
          video_type: editType,
          type: editType,
          youtube_url: editYoutubeUrl || prev.youtube_url
        }));
      }
    } catch (error) {
      console.error('Update error:', error);
      alert('Failed to update video');
    } finally {
      setIsUpdating(false);
    }
  };

  const openVideoModal = (video) => {
    setSelectedVideo(video);
    setIsModalOpen(true);
    setIsPlaying(false);
  };

  const closeVideoModal = () => {
    setIsModalOpen(false);
    setSelectedVideo(null);
    setIsPlaying(false);
    if (videoPlayerRef.current) {
      videoPlayerRef.current.pause();
    }
  };

  const togglePlay = () => {
    if (videoPlayerRef.current) {
      if (isPlaying) {
        videoPlayerRef.current.pause();
      } else {
        videoPlayerRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoPlayerRef.current) {
      videoPlayerRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (videoPlayerRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoPlayerRef.current.requestFullscreen();
      }
    }
  };

  // Get thumbnail URL - prefer embedded base64 (instant, no round-trip) over GridFS URL
  const getThumbnailUrl = (video) => {
    // If thumbnail_data exists AND is non-empty base64, render instantly — no HTTP request
    if (video.thumbnail_data && video.thumbnail_data.startsWith('data:')) {
      return video.thumbnail_data;
    }
    if (video.thumbnail) {
      // If it's an API path, prepend the origin
      if (video.thumbnail.startsWith('/api/')) {
        return `${API_URL}${video.thumbnail}`;
      }
      return video.thumbnail;
    }
    // Return null - let VideoThumbnail component handle the placeholder
    return null;
  };

  // Fallback handled by VideoThumbnail component
  const handleImageError = (e) => {
    // No-op - VideoThumbnail handles errors internally
  };

  // Get video URL for playback
  const getVideoUrl = (video) => {
    if (video.video_url) {
      if (video.video_url.startsWith('/api/')) {
        return `${API_URL}${video.video_url}`;
      }
      return video.video_url;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6 overflow-y-auto relative">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold mb-1">Media Portfolio</h1>
            <p className="text-gray-400 text-xs md:text-sm">My Media Portfolio</p>
          </div>
          <button 
            onClick={() => {
              if (mainMediaTab === 'Videos') {
                setShowUploadModal(true);
              } else if (mainMediaTab === 'Audio') {
                setShowAudioUploadModal(true);
              } else if (mainMediaTab === 'Photos') {
                setShowPhotoUploadModal(true);
              }
            }}
            data-testid="add-media-btn"
            className="flex items-center gap-1.5 md:gap-2 px-4 md:px-6 py-2 md:py-3 bg-black border border-gray-400 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] rounded-lg font-medium transition-all duration-300 text-sm md:text-base text-white"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Add New {mainMediaTab === 'Videos' ? 'Video' : mainMediaTab === 'Audio' ? 'Track' : 'Photo'}</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>

        {/* Main Media Type Tabs: Videos, Photos, Audio */}
        <div className="flex items-center gap-2 mb-4 border-b border-gray-700 pb-3">
          {[
            { id: 'Videos', icon: Video, label: 'Videos' },
            { id: 'Photos', icon: Camera, label: 'Photos' },
            { id: 'Audio', icon: Music, label: 'Audio' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setMainMediaTab(tab.id)}
              data-testid={`media-tab-${tab.id.toLowerCase()}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                mainMediaTab === tab.id
                  ? 'bg-white text-black shadow-lg'
                  : 'bg-black border border-gray-600 text-gray-400 hover:border-gray-400 hover:text-white'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Videos Tab Content */}
        {mainMediaTab === 'Videos' && (
          <>
            {/* Search Bar */}
            <div className="relative mb-4 md:mb-6">
              <Search className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search videos or content..."
                className="w-full pl-10 md:pl-12 pr-4 py-2.5 md:py-3 bg-black border border-gray-400 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gray-300 focus:shadow-[0_0_15px_rgba(156,163,175,0.3)] transition-all duration-300 text-sm"
              />
            </div>

            {/* Category Tabs - Drag to reorder */}
            <div className="flex items-center gap-1.5 md:gap-2 mb-4 md:mb-6 overflow-x-auto pb-2">
              {customMediaCategories
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveTab(cat.id)}
                  draggable={cat.id !== 'All'}
                  onDragStart={(e) => handleDragStart(e, cat)}
                  onDragOver={(e) => handleDragOver(e, cat)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, cat)}
                  onDragEnd={handleDragEnd}
                  data-testid={`media-category-${cat.id}`}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium whitespace-nowrap transition-all duration-300 flex items-center gap-1.5 border ${
                    activeTab === cat.id 
                      ? 'bg-black border-white text-white shadow-[0_0_15px_rgba(255,255,255,0.3)]' 
                      : 'bg-black border-gray-600 text-gray-400 hover:border-gray-400 hover:text-white hover:shadow-[0_0_10px_rgba(156,163,175,0.3)]'
                  } ${cat.id !== 'All' ? 'cursor-grab active:cursor-grabbing' : ''} ${
                    dragOverCategory === cat.id ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-black' : ''
                  } ${draggedCategory?.id === cat.id ? 'opacity-50' : ''}`}
                  title={cat.id !== 'All' ? 'Drag to reorder' : ''}
                >
                  <span>{cat.icon}</span>
                  {cat.name}
                </button>
              ))}
              
              {/* Edit Categories Button */}
              <button
                onClick={() => setShowMediaCategoryModal(true)}
                data-testid="edit-media-categories-btn"
                className="px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all duration-300 whitespace-nowrap bg-black border border-dashed border-gray-500 text-gray-400 hover:border-gray-300 hover:text-white hover:shadow-[0_0_10px_rgba(156,163,175,0.3)] flex items-center gap-1"
              >
                <Pencil size={12} />
                Edit
              </button>
            </div>
          </>
        )}
      </div>

      {/* Videos Tab Content - Story & Library Sections */}
      {mainMediaTab === 'Videos' && (
        <>
      {/* Story Section */}
      <div className="mb-6 md:mb-8">
        <button
          type="button"
          onClick={toggleStoryCollapsed}
          data-testid="media-portfolio-story-toggle"
          aria-expanded={!storyCollapsed}
          className="flex items-center gap-2 mb-3 md:mb-4 text-left hover:text-cyan-300 transition-colors group"
        >
          <ChevronDown
            size={20}
            className={`text-gray-400 group-hover:text-cyan-300 transition-transform ${storyCollapsed ? '-rotate-90' : ''}`}
          />
          <h2 className="text-xl md:text-2xl font-bold">Story ({filteredStoryVideos.length})</h2>
        </button>
        {!storyCollapsed && (isLoading ? (
          <div className="flex items-center justify-center py-8 md:py-12">
            <Loader2 size={28} className="animate-spin text-blue-400" />
          </div>
        ) : filteredStoryVideos.length > 0 ? (
          <div className="flex gap-3 md:gap-4 overflow-x-auto pb-4 pt-2 pl-1 -ml-1">
            {filteredStoryVideos.map((video) => (
              <div
                key={video.id}
                draggable
                onDragStart={(e) => handleVideoDragStart(e, video, 'story')}
                onDragOver={(e) => handleVideoDragOver(e, video, 'story')}
                onDragLeave={handleVideoDragLeave}
                onDrop={(e) => handleVideoDrop(e, video, 'story')}
                onDragEnd={handleVideoDragEnd}
                onClick={() => openVideoModal(video)}
                className={`flex-shrink-0 w-36 md:w-48 bg-black border rounded-xl overflow-hidden transition-all duration-300 cursor-grab active:cursor-grabbing relative group ${
                  dragOverVideo === video.id ? 'border-blue-500 shadow-[0_0_20px_rgba(59,130,246,0.5)]' : 
                  draggedVideo?.id === video.id ? 'opacity-50 border-gray-600' :
                  'border-gray-400 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)]'
                }`}
              >
                {video.isNew && (
                  <div className="absolute top-2 right-2 px-1.5 md:px-2 py-0.5 md:py-1 bg-green-500 rounded text-[10px] md:text-xs font-semibold z-10">NEW</div>
                )}
                <div className="relative aspect-[9/16] bg-gray-800">
                  <VideoThumbnail 
                    src={getThumbnailUrl(video)} 
                    alt={video.title || video.name} 
                    fallbackIcon={Film}
                    iconSize={28}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                    <Play size={36} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                <div className="p-2 md:p-3">
                  <h3 className="font-semibold text-xs md:text-sm mb-2 line-clamp-2">{video.title || video.name}</h3>
                  {/* Category badge below title */}
                  <div className={`inline-block px-2 py-0.5 ${categoryColors[video.category] || video.categoryColor || 'bg-purple-600'} rounded text-[10px] md:text-xs font-semibold`}>
                    {video.category || 'Uncategorized'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 md:py-12 bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <Film size={36} className="mx-auto text-gray-600 mb-3 md:mb-4" />
            <p className="text-gray-400 text-sm md:text-base mb-2">{activeTab === 'All' ? 'No story videos yet' : `No ${activeTab} videos in stories`}</p>
            <p className="text-gray-500 text-xs md:text-sm mb-3 md:mb-4">Upload vertical (9:16) videos for stories</p>
            <button
              onClick={() => { setUploadType('story'); setShowUploadModal(true); }}
              className="px-3 md:px-4 py-1.5 md:py-2 bg-black border border-gray-400 hover:border-gray-300 hover:shadow-[0_0_15px_rgba(156,163,175,0.3)] text-white rounded-lg transition-all duration-300 text-sm"
            >
              Upload Story Video
            </button>
          </div>
        ))}
      </div>

      {/* Full Library Section */}
      <div>
        <button
          type="button"
          onClick={toggleLibraryCollapsed}
          data-testid="media-portfolio-library-toggle"
          aria-expanded={!libraryCollapsed}
          className="flex items-center gap-2 mb-3 md:mb-4 text-left hover:text-cyan-300 transition-colors group"
        >
          <ChevronDown
            size={20}
            className={`text-gray-400 group-hover:text-cyan-300 transition-transform ${libraryCollapsed ? '-rotate-90' : ''}`}
          />
          <h2 className="text-xl md:text-2xl font-bold">Full Library ({filteredLibraryVideos.length})</h2>
        </button>
        {!libraryCollapsed && (isLoading ? (
          <div className="flex items-center justify-center py-8 md:py-12">
            <Loader2 size={28} className="animate-spin text-purple-400" />
          </div>
        ) : filteredLibraryVideos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {filteredLibraryVideos.map((video) => (
              <div
                key={video.id}
                draggable
                onDragStart={(e) => handleVideoDragStart(e, video, 'library')}
                onDragOver={(e) => handleVideoDragOver(e, video, 'library')}
                onDragLeave={handleVideoDragLeave}
                onDrop={(e) => handleVideoDrop(e, video, 'library')}
                onDragEnd={handleVideoDragEnd}
                onClick={() => openVideoModal(video)}
                className={`bg-black border rounded-xl overflow-hidden transition-all duration-300 cursor-grab active:cursor-grabbing relative group ${
                  dragOverVideo === video.id ? 'border-blue-500 shadow-[0_0_20px_rgba(59,130,246,0.5)]' : 
                  draggedVideo?.id === video.id ? 'opacity-50 border-gray-600' :
                  'border-gray-400 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)]'
                }`}
              >
                <div className="relative aspect-video bg-gray-800">
                  <VideoThumbnail 
                    src={getThumbnailUrl(video)} 
                    alt={video.title || video.name} 
                    fallbackIcon={Video}
                    iconSize={36}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                    <Play size={48} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  {video.isNew && (
                    <div className="absolute top-2 md:top-3 left-2 md:left-3 px-2 md:px-3 py-0.5 md:py-1 bg-green-500 rounded text-[10px] md:text-xs font-semibold">NEW</div>
                  )}
                </div>
                <div className="p-3 md:p-4">
                  {/* Category badge moved here below video */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 md:px-3 py-0.5 md:py-1 ${categoryColors[video.category] || video.categoryColor || 'bg-purple-600'} rounded text-[10px] md:text-xs font-semibold`}>
                      {video.category}
                    </span>
                  </div>
                  <h3 className="font-semibold text-sm md:text-base mb-1 line-clamp-2">{video.title || video.name}</h3>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 md:py-16 bg-black border border-gray-400 rounded-xl hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <Video size={48} className="mx-auto text-gray-600 mb-3 md:mb-4" />
            <p className="text-gray-400 text-base md:text-xl mb-2">{activeTab === 'All' ? 'No library videos yet' : `No ${activeTab} videos in library`}</p>
            <p className="text-gray-500 text-sm mb-4 md:mb-6">Upload horizontal (16:9) videos to your library</p>
            <button
              onClick={() => { setUploadType('library'); setShowUploadModal(true); }}
              className="px-4 md:px-6 py-2 md:py-3 bg-black border border-gray-400 hover:border-gray-300 hover:shadow-[0_0_15px_rgba(156,163,175,0.3)] text-white rounded-lg transition-all duration-300 font-semibold text-sm md:text-base"
            >
              Upload Library Video
            </button>
          </div>
        ))}
      </div>
        </>
      )}

      {/* Photos Tab Content */}
      {mainMediaTab === 'Photos' && (
        <div className="space-y-4 pb-8">
          {/* Photos Header with Upload Button */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Camera size={24} className="text-blue-400" />
              Photo Gallery
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCategoryManager(true)}
                className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-all text-sm flex items-center gap-2"
              >
                <Edit size={14} />
                Manage Categories
              </button>
              <button
                onClick={() => setShowPhotoUploadModal(true)}
                data-testid="upload-photo-btn"
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all font-medium flex items-center gap-2 text-sm"
              >
                <Upload size={16} />
                Upload Photos
              </button>
            </div>
          </div>

          {/* Photo Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-gray-800">
            <button
              onClick={() => setActivePhotoCategoryFilter('All')}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activePhotoCategoryFilter === 'All'
                  ? 'bg-white text-black'
                  : 'bg-black text-gray-400 border border-gray-700 hover:border-gray-500'
              }`}
            >
              All Photos
              <span className="ml-2 text-xs opacity-70">({photos.length})</span>
            </button>
            {photoCategories.map((category) => {
              const count = photos.filter(p => p.category === category.name).length;
              return (
                <button
                  key={category.id}
                  onClick={() => setActivePhotoCategoryFilter(category.name)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    activePhotoCategoryFilter === category.name
                      ? 'bg-white text-black'
                      : 'bg-black text-gray-400 border border-gray-700 hover:border-gray-500'
                  }`}
                >
                  {category.name}
                  {count > 0 && <span className="ml-2 text-xs opacity-70">({count})</span>}
                </button>
              );
            })}
          </div>

          {/* Photo Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search photos by title, caption, or tags..."
              value={photoSearchQuery}
              onChange={(e) => setPhotoSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gray-400 transition-all"
            />
          </div>

          {/* Photo Grid */}
          {photosLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-blue-400" />
            </div>
          ) : filteredPhotos.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filteredPhotos.map((photo) => (
                <div
                  key={photo.id}
                  className="group relative aspect-square bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-white/50 hover:shadow-[0_0_20px_rgba(255,255,255,0.15)] transition-all duration-300 cursor-pointer"
                  onClick={() => {
                    setSelectedPhoto(photo);
                    setShowPhotoViewer(true);
                  }}
                  data-testid={`photo-${photo.id}`}
                >
                  <img
                    src={`${API_URL}${photo.photo_url}`}
                    alt={photo.title || 'Photo'}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                    decoding="async"
                  />
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditPhotoModal(photo);
                        }}
                        className="p-2 bg-blue-600/80 hover:bg-blue-600 rounded-lg transition-colors"
                        title="Edit photo"
                      >
                        <Edit size={16} className="text-white" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Delete this photo?')) {
                            handleDeletePhoto(photo.id);
                          }
                        }}
                        className="p-2 bg-red-600/80 hover:bg-red-600 rounded-lg transition-colors"
                        title="Delete photo"
                      >
                        <Trash2 size={16} className="text-white" />
                      </button>
                    </div>
                    <div>
                      {photo.title && (
                        <p className="text-white font-medium text-sm truncate">{photo.title}</p>
                      )}
                      {photo.category && (
                        <p className="text-gray-300 text-xs">{photo.category}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-900/30 border border-gray-800 rounded-xl">
              <Camera size={64} className="mx-auto text-gray-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-400 mb-2">No Photos Yet</h2>
              <p className="text-gray-500 mb-6">Upload your photos to build your portfolio</p>
              <button
                onClick={() => setShowPhotoUploadModal(true)}
                data-testid="upload-first-photo-btn"
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all font-semibold flex items-center gap-2 mx-auto"
              >
                <Upload size={18} />
                Upload Your First Photo
              </button>
            </div>
          )}
        </div>
      )}

      {/* Photo Viewer Modal */}
      {showPhotoViewer && selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPhotoViewer(false)}
        >
          <button
            onClick={() => setShowPhotoViewer(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <X size={24} className="text-white" />
          </button>
          <div className="max-w-5xl max-h-[90vh] relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={`${API_URL}${selectedPhoto.photo_url}`}
              alt={selectedPhoto.title || 'Photo'}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
              loading="eager"
              decoding="async"
            />
            {(selectedPhoto.title || selectedPhoto.caption) && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 rounded-b-lg">
                {selectedPhoto.title && (
                  <h3 className="text-white font-semibold text-lg">{selectedPhoto.title}</h3>
                )}
                {selectedPhoto.caption && (
                  <p className="text-gray-300 text-sm mt-1">{selectedPhoto.caption}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audio Tab Content - BeatStars Style */}
      {mainMediaTab === 'Audio' && (
        <div className="space-y-4 pb-24">
          {/* Audio Header with Share Store Button */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Music size={24} className="text-purple-500 flex-shrink-0" />
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-white">Beat Store</h2>
                <p className="text-xs sm:text-sm text-gray-400">{audioTracks.length} tracks available</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="bulk-upload-btn"
                onClick={() => { setBulkUploadFiles([]); setShowBulkUploadModal(true); }}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded-lg text-white text-sm font-medium transition-colors"
              >
                <Upload size={16} />
                <span className="hidden sm:inline">Bulk Upload</span>
                <span className="sm:hidden">Bulk</span>
              </button>
              <button
                onClick={() => {
                  const storeUrl = `${window.location.origin}/beats/${userId}`;
                  navigator.clipboard.writeText(storeUrl);
                  toast.success(
                    <div className="flex flex-col gap-1">
                      <span className="font-semibold">Store link copied!</span>
                      <span className="text-sm text-gray-300">Share this link with artists to browse and buy your beats</span>
                    </div>
                  );
                }}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm font-medium transition-colors"
                data-testid="share-store-btn"
              >
                <Share2 size={16} />
                <span className="hidden sm:inline">Share Store</span>
                <span className="sm:hidden">Share</span>
                <ExternalLink size={14} className="opacity-70 hidden sm:inline" />
              </button>
            </div>
          </div>

          {/* Sub-tabs: Tracks | Playlists + View Mode Toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-1 p-1 bg-gray-900 border border-gray-800 rounded-xl w-fit">
              <button
                data-testid="audio-sub-tab-tracks"
                onClick={() => setAudioSubTab('tracks')}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                  audioSubTab === 'tracks' ? 'bg-white text-black' : 'text-gray-400 hover:text-white'
                }`}
              >
                <Music size={14} className="sm:w-[15px] sm:h-[15px]" />
                Tracks
                <span className="text-xs opacity-70">({audioTracks.length})</span>
              </button>
              <button
                data-testid="audio-sub-tab-playlists"
                onClick={() => { setAudioSubTab('playlists'); fetchPlaylists(); }}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                  audioSubTab === 'playlists' ? 'bg-white text-black' : 'text-gray-400 hover:text-white'
                }`}
              >
                <ListMusic size={14} className="sm:w-[15px] sm:h-[15px]" />
                Playlists
                <span className="text-xs opacity-70">({playlists.length})</span>
              </button>
            </div>

            {/* View Mode Toggle - Only show for Tracks */}
            {audioSubTab === 'tracks' && (
              <div className="flex items-center gap-1 p-1 bg-gray-900 border border-gray-800 rounded-xl w-fit" data-testid="audio-view-toggle">
                <button
                  onClick={() => { setAudioViewMode('list'); localStorage.setItem('audioViewMode', 'list'); }}
                  className={`p-2 rounded-lg transition-colors ${audioViewMode === 'list' ? 'bg-white text-black' : 'text-gray-400 hover:text-white'}`}
                  title="List View"
                  data-testid="audio-view-list-btn"
                >
                  <List size={18} />
                </button>
                <button
                  onClick={() => { setAudioViewMode('grid'); localStorage.setItem('audioViewMode', 'grid'); }}
                  className={`p-2 rounded-lg transition-colors ${audioViewMode === 'grid' ? 'bg-white text-black' : 'text-gray-400 hover:text-white'}`}
                  title="Grid View"
                  data-testid="audio-view-grid-btn"
                >
                  <LayoutGrid size={18} />
                </button>
              </div>
            )}
          </div>

          {/* ---- TRACKS PANEL ---- */}
          {audioSubTab === 'tracks' && <>

          {/* Audio Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search tracks, artists, or tags..."
              value={audioSearchQuery}
              onChange={(e) => setAudioSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gray-400 transition-all"
            />
          </div>

          {/* Horizontal Filters Bar */}
          <div className="flex items-center gap-2 flex-wrap pb-2 border-b border-gray-800">
            {/* Genre Filter */}
            <div className="relative">
              <button
                onClick={() => setOpenFilterDropdown(openFilterDropdown === 'genre' ? null : 'genre')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all border ${
                  audioFilters.genre 
                    ? 'bg-white text-black border-white' 
                    : 'bg-black text-gray-400 border-gray-700 hover:border-gray-500'
                }`}
              >
                <Headphones size={16} />
                {audioFilters.genre || 'Genre'}
                <ChevronDown size={14} className={`transition-transform ${openFilterDropdown === 'genre' ? 'rotate-180' : ''}`} />
              </button>
              {openFilterDropdown === 'genre' && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-black border border-gray-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => { setAudioFilters(prev => ({ ...prev, genre: '' })); setOpenFilterDropdown(null); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-400 hover:bg-gray-800 hover:text-white"
                  >
                    All Genres
                  </button>
                  {filterOptions.genre.map((option) => (
                    <button
                      key={option}
                      onClick={() => { setAudioFilters(prev => ({ ...prev, genre: option })); setOpenFilterDropdown(null); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-800 ${audioFilters.genre === option ? 'text-white bg-gray-800' : 'text-gray-400'}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Track Type Filter */}
            <div className="relative">
              <button
                onClick={() => setOpenFilterDropdown(openFilterDropdown === 'trackType' ? null : 'trackType')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all border ${
                  audioFilters.trackType 
                    ? 'bg-white text-black border-white' 
                    : 'bg-black text-gray-400 border-gray-700 hover:border-gray-500'
                }`}
              >
                <Music size={16} />
                {audioFilters.trackType || 'Track Type'}
                <ChevronDown size={14} className={`transition-transform ${openFilterDropdown === 'trackType' ? 'rotate-180' : ''}`} />
              </button>
              {openFilterDropdown === 'trackType' && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-black border border-gray-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => { setAudioFilters(prev => ({ ...prev, trackType: '' })); setOpenFilterDropdown(null); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-400 hover:bg-gray-800 hover:text-white"
                  >
                    All Types
                  </button>
                  {filterOptions.trackType.map((option) => (
                    <button
                      key={option}
                      onClick={() => { setAudioFilters(prev => ({ ...prev, trackType: option })); setOpenFilterDropdown(null); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-800 ${audioFilters.trackType === option ? 'text-white bg-gray-800' : 'text-gray-400'}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Price Filter */}
            <div className="relative">
              <button
                onClick={() => setOpenFilterDropdown(openFilterDropdown === 'price' ? null : 'price')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all border ${
                  audioFilters.price 
                    ? 'bg-white text-black border-white' 
                    : 'bg-black text-gray-400 border-gray-700 hover:border-gray-500'
                }`}
              >
                <DollarSign size={16} />
                {audioFilters.price || 'Price'}
                <ChevronDown size={14} className={`transition-transform ${openFilterDropdown === 'price' ? 'rotate-180' : ''}`} />
              </button>
              {openFilterDropdown === 'price' && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-black border border-gray-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => { setAudioFilters(prev => ({ ...prev, price: '' })); setOpenFilterDropdown(null); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-400 hover:bg-gray-800 hover:text-white"
                  >
                    Any Price
                  </button>
                  {filterOptions.price.map((option) => (
                    <button
                      key={option}
                      onClick={() => { setAudioFilters(prev => ({ ...prev, price: option })); setOpenFilterDropdown(null); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-800 ${audioFilters.price === option ? 'text-white bg-gray-800' : 'text-gray-400'}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mood Filter */}
            <div className="relative">
              <button
                onClick={() => setOpenFilterDropdown(openFilterDropdown === 'mood' ? null : 'mood')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all border ${
                  audioFilters.mood 
                    ? 'bg-white text-black border-white' 
                    : 'bg-black text-gray-400 border-gray-700 hover:border-gray-500'
                }`}
              >
                <Smile size={16} />
                {audioFilters.mood || 'Mood'}
                <ChevronDown size={14} className={`transition-transform ${openFilterDropdown === 'mood' ? 'rotate-180' : ''}`} />
              </button>
              {openFilterDropdown === 'mood' && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-black border border-gray-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => { setAudioFilters(prev => ({ ...prev, mood: '' })); setOpenFilterDropdown(null); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-400 hover:bg-gray-800 hover:text-white"
                  >
                    Any Mood
                  </button>
                  {filterOptions.mood.map((option) => (
                    <button
                      key={option}
                      onClick={() => { setAudioFilters(prev => ({ ...prev, mood: option })); setOpenFilterDropdown(null); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-800 ${audioFilters.mood === option ? 'text-white bg-gray-800' : 'text-gray-400'}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Key Filter */}
            <div className="relative">
              <button
                onClick={() => setOpenFilterDropdown(openFilterDropdown === 'key' ? null : 'key')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all border ${
                  audioFilters.key 
                    ? 'bg-white text-black border-white' 
                    : 'bg-black text-gray-400 border-gray-700 hover:border-gray-500'
                }`}
              >
                <Hash size={16} />
                {audioFilters.key || 'Key'}
                <ChevronDown size={14} className={`transition-transform ${openFilterDropdown === 'key' ? 'rotate-180' : ''}`} />
              </button>
              {openFilterDropdown === 'key' && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-black border border-gray-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => { setAudioFilters(prev => ({ ...prev, key: '' })); setOpenFilterDropdown(null); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-400 hover:bg-gray-800 hover:text-white"
                  >
                    Any Key
                  </button>
                  {filterOptions.key.map((option) => (
                    <button
                      key={option}
                      onClick={() => { setAudioFilters(prev => ({ ...prev, key: option })); setOpenFilterDropdown(null); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-800 ${audioFilters.key === option ? 'text-white bg-gray-800' : 'text-gray-400'}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Instrument Filter */}
            <div className="relative">
              <button
                onClick={() => setOpenFilterDropdown(openFilterDropdown === 'instrument' ? null : 'instrument')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all border ${
                  audioFilters.instrument 
                    ? 'bg-white text-black border-white' 
                    : 'bg-black text-gray-400 border-gray-700 hover:border-gray-500'
                }`}
              >
                <Guitar size={16} />
                {audioFilters.instrument || 'Instrument'}
                <ChevronDown size={14} className={`transition-transform ${openFilterDropdown === 'instrument' ? 'rotate-180' : ''}`} />
              </button>
              {openFilterDropdown === 'instrument' && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-black border border-gray-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => { setAudioFilters(prev => ({ ...prev, instrument: '' })); setOpenFilterDropdown(null); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-400 hover:bg-gray-800 hover:text-white"
                  >
                    Any Instrument
                  </button>
                  {filterOptions.instrument.map((option) => (
                    <button
                      key={option}
                      onClick={() => { setAudioFilters(prev => ({ ...prev, instrument: option })); setOpenFilterDropdown(null); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-800 ${audioFilters.instrument === option ? 'text-white bg-gray-800' : 'text-gray-400'}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* BPM Filter */}
            <div className="relative">
              <button
                onClick={() => setOpenFilterDropdown(openFilterDropdown === 'bpm' ? null : 'bpm')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all border ${
                  audioFilters.bpm 
                    ? 'bg-white text-black border-white' 
                    : 'bg-black text-gray-400 border-gray-700 hover:border-gray-500'
                }`}
              >
                <Activity size={16} />
                {audioFilters.bpm || 'BPM'}
                <ChevronDown size={14} className={`transition-transform ${openFilterDropdown === 'bpm' ? 'rotate-180' : ''}`} />
              </button>
              {openFilterDropdown === 'bpm' && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-black border border-gray-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => { setAudioFilters(prev => ({ ...prev, bpm: '' })); setOpenFilterDropdown(null); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-400 hover:bg-gray-800 hover:text-white"
                  >
                    Any BPM
                  </button>
                  {filterOptions.bpm.map((option) => (
                    <button
                      key={option}
                      onClick={() => { setAudioFilters(prev => ({ ...prev, bpm: option })); setOpenFilterDropdown(null); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-800 ${audioFilters.bpm === option ? 'text-white bg-gray-800' : 'text-gray-400'}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Duration Filter */}
            <div className="relative">
              <button
                onClick={() => setOpenFilterDropdown(openFilterDropdown === 'duration' ? null : 'duration')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all border ${
                  audioFilters.duration 
                    ? 'bg-white text-black border-white' 
                    : 'bg-black text-gray-400 border-gray-700 hover:border-gray-500'
                }`}
              >
                <Clock size={16} />
                {audioFilters.duration || 'Duration'}
                <ChevronDown size={14} className={`transition-transform ${openFilterDropdown === 'duration' ? 'rotate-180' : ''}`} />
              </button>
              {openFilterDropdown === 'duration' && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-black border border-gray-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => { setAudioFilters(prev => ({ ...prev, duration: '' })); setOpenFilterDropdown(null); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-400 hover:bg-gray-800 hover:text-white"
                  >
                    Any Duration
                  </button>
                  {filterOptions.duration.map((option) => (
                    <button
                      key={option}
                      onClick={() => { setAudioFilters(prev => ({ ...prev, duration: option })); setOpenFilterDropdown(null); }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-800 ${audioFilters.duration === option ? 'text-white bg-gray-800' : 'text-gray-400'}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Date Sort */}
            <div className="relative">
              <button
                data-testid="sort-date-btn"
                onClick={() => setOpenFilterDropdown(openFilterDropdown === 'sortDate' ? null : 'sortDate')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all border ${
                  audioSortBy
                    ? 'bg-white text-black border-white'
                    : 'bg-black text-gray-400 border-gray-700 hover:border-gray-500'
                }`}
              >
                <Calendar size={16} />
                {audioSortBy === 'date_newest' ? 'Newest First' : audioSortBy === 'date_oldest' ? 'Oldest First' : 'Date'}
                <ChevronDown size={14} className={`transition-transform ${openFilterDropdown === 'sortDate' ? 'rotate-180' : ''}`} />
              </button>
              {openFilterDropdown === 'sortDate' && (
                <div className="absolute top-full left-0 mt-1 w-44 bg-black border border-gray-700 rounded-lg shadow-xl z-50">
                  <button
                    onClick={() => { setAudioSortBy(''); setOpenFilterDropdown(null); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-400 hover:bg-gray-800 hover:text-white"
                  >
                    Default Order
                  </button>
                  <button
                    onClick={() => { setAudioSortBy('date_newest'); setOpenFilterDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-800 ${audioSortBy === 'date_newest' ? 'text-white bg-gray-800' : 'text-gray-400'}`}
                  >
                    Newest First
                  </button>
                  <button
                    onClick={() => { setAudioSortBy('date_oldest'); setOpenFilterDropdown(null); }}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-800 ${audioSortBy === 'date_oldest' ? 'text-white bg-gray-800' : 'text-gray-400'}`}
                  >
                    Oldest First
                  </button>
                </div>
              )}
            </div>

            {/* Clear All Filters */}
            {(Object.values(audioFilters).some(v => v) || audioTagFilter || audioSortBy) && (
              <button
                onClick={() => {
                  setAudioFilters({ genre: '', trackType: '', price: '', mood: '', key: '', instrument: '', bpm: '', duration: '' });
                  setAudioTagFilter('');
                  setAudioSortBy('');
                }}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-red-400 hover:text-red-300 whitespace-nowrap flex items-center gap-2"
              >
                Clear All
                {activeFilterCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{activeFilterCount}</span>
                )}
              </button>
            )}
          </div>

          {/* Tag Pills (if available) */}
          {allAudioTags.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto py-2">
              <span className="text-xs text-gray-500 mr-2">Popular:</span>
              {allAudioTags.slice(0, 8).map((tagObj) => (
                <button
                  key={tagObj.tag}
                  onClick={() => { setAudioTagFilter(audioTagFilter === tagObj.tag ? '' : tagObj.tag); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                    audioTagFilter === tagObj.tag ? 'bg-white text-black' : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  #{tagObj.tag}
                </button>
              ))}
            </div>
          )}

          {/* Filter Results Count */}
          {(activeFilterCount > 0 || audioSearchQuery) && (
            <div className="flex items-center justify-between py-2">
              <p className="text-sm text-gray-400">
                Showing <span className="text-white font-medium">{filteredAudioTracks.length}</span> of <span className="text-white">{audioTracks.length}</span> tracks
              </p>
            </div>
          )}

          {/* Track List / Grid View */}
          {audioLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-purple-400" />
            </div>
          ) : filteredAudioTracks.length > 0 ? (
            <>
              {/* Bulk Action Bar */}
              {selectedTrackIds.size > 0 && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 px-3 sm:px-4 py-3 bg-white/5 border border-white/20 rounded-xl">
                  <span className="text-sm font-medium text-white">{selectedTrackIds.size} selected</span>
                  <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
                    <button
                      data-testid="bulk-edit-selected-btn"
                      onClick={() => setShowBulkEditModal(true)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/40 text-blue-300 rounded-lg text-xs sm:text-sm transition-colors"
                    >
                      <Edit size={14} />
                      <span className="hidden xs:inline">Edit</span>
                    </button>
                    <button
                      data-testid="bulk-delete-selected-btn"
                      onClick={handleBulkDelete}
                      disabled={isBulkDeleting}
                      className="flex items-center gap-2 px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 border border-red-500/40 text-red-300 rounded-lg text-xs sm:text-sm transition-colors disabled:opacity-50"
                    >
                      {isBulkDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      <span className="hidden xs:inline">Delete</span>
                    </button>
                    <button
                      onClick={() => setSelectedTrackIds(new Set())}
                      className="p-1.5 text-gray-400 hover:text-white rounded-lg transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* ========== LIST VIEW ========== */}
              {audioViewMode === 'list' && (
                <div data-testid="audio-list-view">
              {/* Select All Row */}
              <div className="flex items-center gap-3 px-1 pb-1 border-b border-gray-800">
                <input
                  type="checkbox"
                  data-testid="select-all-tracks"
                  checked={filteredAudioTracks.slice((audioPage - 1) * TRACKS_PER_PAGE, audioPage * TRACKS_PER_PAGE).length > 0 &&
                    filteredAudioTracks.slice((audioPage - 1) * TRACKS_PER_PAGE, audioPage * TRACKS_PER_PAGE).every(t => selectedTrackIds.has(t.id))}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded accent-white cursor-pointer"
                />
                <span className="text-xs text-gray-500">Select All</span>
              </div>

              {/* Scrollable Track Container - Shows 12 tracks before scrolling */}
              <div 
                className="space-y-2 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent"
                style={{ maxHeight: `${VISIBLE_TRACKS * 82}px` }} // ~82px per track (padding + content)
              >
                {filteredAudioTracks
                  .slice((audioPage - 1) * TRACKS_PER_PAGE, audioPage * TRACKS_PER_PAGE)
                  .map((track, index) => {
                    // index is the position within the current page's displayed tracks
                    const displayNumber = (audioPage - 1) * TRACKS_PER_PAGE + index + 1;
                    return (
                <div
                  key={track.id}
                  data-testid={`audio-track-${track.id}`}
                  draggable
                        onDragStart={(e) => handleTrackDragStart(e, index)}
                  onDragEnd={handleTrackDragEnd}
                        onDragOver={(e) => handleTrackDragOver(e, index)}
                        onDrop={(e) => handleTrackDrop(e, index)}
                  className={`group rounded-xl p-3 sm:p-4 transition-all duration-300 cursor-grab active:cursor-grabbing ${
                    currentlyPlaying?.id === track.id 
                      ? 'bg-black border-2 border-white shadow-[0_0_25px_rgba(255,255,255,0.25)]' 
                          : draggedTrackIndex === index
                        ? 'bg-gray-800 border-2 border-dashed border-purple-500'
                        : 'bg-black border border-gray-700 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.15)]'
                  }`}
                  onClick={() => playTrack(track)}
                >
                  {/* Mobile Layout */}
                  <div className="flex sm:hidden items-start gap-3">
                    {/* Cover Art */}
                    <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                      {track.cover_url ? (
                        <OptimizedImage
                          src={`${API_URL}${track.cover_url}`}
                          alt=""
                          className="w-12 h-12 rounded-lg"
                          aspectRatio=""
                          fallbackIcon={Music}
                          fallbackClassName="text-gray-600"
                          placeholderColor="bg-gray-800"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600">
                          <Music size={20} className="text-white" />
                        </div>
                      )}
                      {/* Play Overlay */}
                      <div className={`absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity ${
                        currentlyPlaying?.id === track.id ? 'opacity-100' : 'opacity-0'
                      }`}>
                        {currentlyPlaying?.id === track.id && audioIsPlaying ? (
                          <Pause size={18} className="text-white" />
                        ) : (
                          <Play size={18} className="text-white ml-0.5" />
                        )}
                      </div>
                    </div>
                    
                    {/* Track Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold text-sm truncate ${
                        currentlyPlaying?.id === track.id ? 'text-white' : 'text-white'
                      }`}>
                        {track.title}
                      </h3>
                      <p className="text-xs text-gray-400 truncate">{track.artist}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {track.bpm && <span className="text-xs text-gray-500">{track.bpm} BPM</span>}
                        {track.key && <span className="text-xs text-gray-500">• {track.key}</span>}
                      </div>
                    </div>
                    
                    {/* Price + Actions */}
                    <div className="flex flex-col items-end gap-2">
                      {track.is_free ? (
                        <span className="px-2 py-1 bg-green-600/20 text-green-400 rounded text-xs font-medium">FREE</span>
                      ) : track.price ? (
                        <span className="px-2 py-1 bg-blue-600/20 text-blue-400 rounded text-xs font-medium">${track.price}</span>
                      ) : null}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); openEditTrackModal(track); }}
                          className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                        >
                          <Edit size={14} className="text-gray-400" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteTrack(track.id); }}
                          className="p-1.5 hover:bg-red-900/50 rounded-lg transition-colors"
                        >
                          <Trash2 size={14} className="text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden sm:flex items-center gap-4">
                    {/* Checkbox + Drag Handle + Track Number */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        data-testid={`select-track-${track.id}`}
                        checked={selectedTrackIds.has(track.id)}
                        onChange={(e) => toggleTrackSelection(e, track.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded accent-white cursor-pointer flex-shrink-0"
                      />
                      <span className="text-gray-600 cursor-grab">⋮⋮</span>
                      <span className={`w-6 text-center text-sm font-mono transition-colors ${
                        currentlyPlaying?.id === track.id ? 'text-white' : 'text-gray-500'
                            }`}>{displayNumber}</span>
                    </div>
                    
                    {/* Cover Art / Play Button */}
                    <div 
                      className="relative w-14 h-14 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0 group/cover"
                    >
                      {track.cover_url ? (
                        <OptimizedImage
                          src={`${API_URL}${track.cover_url}`}
                          alt=""
                          className="w-14 h-14 rounded-lg"
                          aspectRatio=""
                          fallbackIcon={Music}
                          fallbackClassName="text-gray-600"
                          placeholderColor="bg-gray-800"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600">
                          <Music size={24} className="text-white" />
                        </div>
                      )}
                      {/* Play Overlay */}
                      <div className={`absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity ${
                        currentlyPlaying?.id === track.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                      }`}>
                        {currentlyPlaying?.id === track.id && audioIsPlaying ? (
                          <Pause size={24} className="text-white" />
                        ) : (
                          <Play size={24} className="text-white ml-1" />
                        )}
                      </div>
                    </div>
                    
                    {/* Track Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-semibold truncate transition-colors ${
                        currentlyPlaying?.id === track.id ? 'text-white' : 'text-white group-hover:text-gray-200'
                      }`}>
                        {track.title}
                      </h3>
                      <p className="text-sm text-gray-400 truncate">{track.artist}</p>
                    </div>
                    
                    {/* Metadata */}
                    <div className="hidden md:flex items-center gap-4 text-sm text-gray-400">
                      {track.published_date && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-white/5 border border-white/10 rounded text-xs">
                          {new Date(track.published_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )}
                      {track.duration && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-white/5 border border-white/10 rounded font-mono">
                          <Clock size={14} />
                          {Math.floor(track.duration / 60)}:{String(Math.floor(track.duration % 60)).padStart(2, '0')}
                        </span>
                      )}
                      {track.bpm && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-white/5 border border-white/10 rounded">
                          <Activity size={14} />
                          {track.bpm} BPM
                        </span>
                      )}
                      {track.key && (
                        <span className="px-2 py-1 bg-white/5 border border-white/10 rounded text-xs">{track.key}</span>
                      )}
                    </div>
                    
                    {/* Tags */}
                    <div className="hidden lg:flex items-center gap-2 flex-wrap max-w-xs">
                      {track.tags?.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-xs text-gray-400 hover:text-white cursor-pointer transition-all"
                          onClick={(e) => { e.stopPropagation(); setAudioTagFilter(tag); fetchAudioTracks(); }}
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                    
                    {/* Price / Free */}
                    <div className="flex items-center gap-2">
                      {track.is_free ? (
                        <span className="px-3 py-1.5 bg-green-600/20 text-green-400 rounded-lg text-sm font-medium flex items-center gap-1">
                          <Download size={14} />
                          FREE
                        </span>
                      ) : track.price ? (
                        <span className="px-3 py-1.5 bg-blue-600/20 text-blue-400 rounded-lg text-sm font-medium">
                          ${track.price}
                        </span>
                      ) : null}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditTrackModal(track);
                        }}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        data-testid={`edit-track-${track.id}`}
                      >
                        <Edit size={16} className="text-gray-400 hover:text-white" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTrack(track.id);
                        }}
                        className="p-2 hover:bg-red-900/50 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} className="text-red-400" />
                      </button>
                    </div>
                  </div>
                </div>
                    );
                  })}
              </div>
              
              {/* Pagination Controls */}
              {filteredAudioTracks.length > TRACKS_PER_PAGE && (
                <div className="flex flex-col sm:flex-row items-center justify-center gap-2 pt-4 border-t border-gray-800 mt-4">
                  {/* Mobile: Simplified pagination */}
                  <div className="flex sm:hidden items-center gap-2 w-full justify-between">
                    <button
                      onClick={() => setAudioPage(p => Math.max(1, p - 1))}
                      disabled={audioPage === 1}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-gray-800 text-gray-300 hover:bg-gray-700"
                    >
                      Prev
                    </button>
                    <span className="text-sm text-gray-400">
                      {audioPage} / {Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE)}
                    </span>
                    <button
                      onClick={() => setAudioPage(p => Math.min(Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE), p + 1))}
                      disabled={audioPage >= Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE)}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-gray-800 text-gray-300 hover:bg-gray-700"
                    >
                      Next
                    </button>
                  </div>
                  
                  {/* Desktop: Full pagination */}
                  <div className="hidden sm:flex items-center gap-2">
                  <button
                    onClick={() => setAudioPage(1)}
                    disabled={audioPage === 1}
                    className="px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-gray-800 text-gray-300 hover:bg-gray-700"
                  >
                    First
                  </button>
                  <button
                    onClick={() => setAudioPage(p => Math.max(1, p - 1))}
                    disabled={audioPage === 1}
                    className="px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-gray-800 text-gray-300 hover:bg-gray-700"
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE)) }, (_, i) => {
                      const totalPages = Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE);
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (audioPage <= 3) {
                        pageNum = i + 1;
                      } else if (audioPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = audioPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setAudioPage(pageNum)}
                          className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                            audioPage === pageNum 
                              ? 'bg-white text-black' 
                              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => setAudioPage(p => Math.min(Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE), p + 1))}
                    disabled={audioPage >= Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE)}
                    className="px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-gray-800 text-gray-300 hover:bg-gray-700"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setAudioPage(Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE))}
                    disabled={audioPage >= Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE)}
                    className="px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-gray-800 text-gray-300 hover:bg-gray-700"
                  >
                    Last
                  </button>
                  
                  <span className="ml-4 text-sm text-gray-500">
                    Page {audioPage} of {Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE)}
                  </span>
                  </div>
                </div>
              )}
                </div>
              )}

              {/* ========== GRID VIEW ========== */}
              {audioViewMode === 'grid' && (
                <>
                  {/* Grid Container */}
                  <div 
                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" 
                    data-testid="audio-grid-view"
                  >
                    {filteredAudioTracks
                      .slice((audioPage - 1) * TRACKS_PER_PAGE, audioPage * TRACKS_PER_PAGE)
                      .map((track, index) => {
                        const isCurrentTrack = currentlyPlaying?.id === track.id;
                        const isPlaying = currentlyPlaying?.id === track.id && audioIsPlaying;
                        
                        return (
                          <div
                            key={track.id}
                            data-testid={`audio-grid-card-${track.id}`}
                            onClick={() => playTrack(track)}
                            className="group cursor-pointer"
                          >
                            {/* Album Art with Play Button Overlay */}
                            <div className="relative aspect-square rounded-lg overflow-hidden mb-3 bg-gray-900">
                              {track.cover_url ? (
                                <OptimizedImage
                                  src={`${API_URL}${track.cover_url}`}
                                  alt={track.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                  aspectRatio=""
                                  fallbackIcon={Music}
                                  fallbackClassName="text-gray-600"
                                  placeholderColor="bg-gray-900"
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-900 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                                  <Music size={40} className="text-gray-600" />
                                </div>
                              )}
                              
                              {/* Play Button Overlay - Bottom Right */}
                              <div className={`absolute bottom-1.5 sm:bottom-2 right-1.5 sm:right-2 transition-all duration-200 ${isPlaying ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0'}`}>
                                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-xl ${isPlaying ? 'bg-white' : 'bg-white'}`}>
                                  {isPlaying ? (
                                    <Pause size={20} className="text-black sm:w-6 sm:h-6" />
                                  ) : (
                                    <Play size={20} className="text-black ml-0.5 sm:w-6 sm:h-6 sm:ml-1" />
                                  )}
                                </div>
                              </div>
                              
                              {/* Now Playing Indicator */}
                              {isPlaying && (
                                <div className="absolute top-2 left-2 flex items-center gap-0.5 bg-white px-2 py-1 rounded-full">
                                  <div className="w-0.5 h-2 bg-black rounded-full animate-pulse" />
                                  <div className="w-0.5 h-3 bg-black rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                                  <div className="w-0.5 h-2 bg-black rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
                                </div>
                              )}

                              {/* Price Badge */}
                              {track.is_free ? (
                                <div className="absolute top-2 right-2 px-2 py-1 bg-green-600/90 rounded text-xs font-semibold text-white">
                                  FREE
                                </div>
                              ) : track.price ? (
                                <div className="absolute top-2 right-2 px-2 py-1 bg-blue-600/90 rounded text-xs font-semibold text-white">
                                  ${track.price}
                                </div>
                              ) : null}
                            </div>
                            
                            {/* Track Info */}
                            <h4 className={`font-medium text-sm truncate mb-1 ${isCurrentTrack ? 'text-white' : 'text-white'}`}>
                              {track.title}
                            </h4>
                            <p className="text-gray-400 text-xs truncate">
                              {track.artist || 'Unknown Artist'}
                            </p>
                            {/* Track metadata */}
                            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                              {track.bpm && <span>{track.bpm} BPM</span>}
                              {track.key && <span>• {track.key}</span>}
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  {/* Grid View Pagination */}
                  {filteredAudioTracks.length > TRACKS_PER_PAGE && (
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-2 pt-4 border-t border-gray-800 mt-4">
                      {/* Mobile: Simplified pagination */}
                      <div className="flex sm:hidden items-center gap-2 w-full justify-between">
                        <button
                          onClick={() => setAudioPage(p => Math.max(1, p - 1))}
                          disabled={audioPage === 1}
                          className="px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-gray-800 text-gray-300 hover:bg-gray-700"
                        >
                          Prev
                        </button>
                        <span className="text-sm text-gray-400">
                          {audioPage} / {Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE)}
                        </span>
                        <button
                          onClick={() => setAudioPage(p => Math.min(Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE), p + 1))}
                          disabled={audioPage >= Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE)}
                          className="px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-gray-800 text-gray-300 hover:bg-gray-700"
                        >
                          Next
                        </button>
                      </div>
                      
                      {/* Desktop: Full pagination */}
                      <div className="hidden sm:flex items-center gap-2">
                      <button
                        onClick={() => setAudioPage(1)}
                        disabled={audioPage === 1}
                        className="px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-gray-800 text-gray-300 hover:bg-gray-700"
                      >
                        First
                      </button>
                      <button
                        onClick={() => setAudioPage(p => Math.max(1, p - 1))}
                        disabled={audioPage === 1}
                        className="px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-gray-800 text-gray-300 hover:bg-gray-700"
                      >
                        Previous
                      </button>
                      
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE)) }, (_, i) => {
                          const totalPages = Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE);
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (audioPage <= 3) {
                            pageNum = i + 1;
                          } else if (audioPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = audioPage - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setAudioPage(pageNum)}
                              className={`w-10 h-10 rounded-lg text-sm font-medium transition-all ${
                                audioPage === pageNum 
                                  ? 'bg-white text-black' 
                                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => setAudioPage(p => Math.min(Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE), p + 1))}
                        disabled={audioPage >= Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE)}
                        className="px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-gray-800 text-gray-300 hover:bg-gray-700"
                      >
                        Next
                      </button>
                      <button
                        onClick={() => setAudioPage(Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE))}
                        disabled={audioPage >= Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE)}
                        className="px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-gray-800 text-gray-300 hover:bg-gray-700"
                      >
                        Last
                      </button>
                      
                      <span className="ml-4 text-sm text-gray-500">
                        Page {audioPage} of {Math.ceil(filteredAudioTracks.length / TRACKS_PER_PAGE)}
                      </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <div className="text-center py-16 bg-gray-900/30 border border-gray-800 rounded-xl">
              <Music size={64} className="mx-auto text-gray-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-400 mb-2">No Tracks Yet</h2>
              <p className="text-gray-500 mb-6">Upload your instrumentals, beats, and audio tracks</p>
              <button
                onClick={() => setShowAudioUploadModal(true)}
                data-testid="upload-first-track-btn"
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all font-semibold flex items-center gap-2 mx-auto"
              >
                <Upload size={18} />
                Upload Your First Track
              </button>
            </div>
          )}

          </> /* end TRACKS PANEL */}

          {/* ---- PLAYLISTS PANEL ---- */}
          {audioSubTab === 'playlists' && (
            <div className="space-y-4">
              {/* Playlists header */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-400">{playlists.length} playlist{playlists.length !== 1 ? 's' : ''}</p>
                <button
                  data-testid="create-playlist-btn"
                  onClick={openCreatePlaylist}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white text-black rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors"
                >
                  <Plus size={16} />
                  New Playlist
                </button>
              </div>

              {playlistsLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 size={28} className="animate-spin text-gray-500" /></div>
              ) : playlists.length === 0 ? (
                <div className="text-center py-16 bg-gray-900/30 border border-gray-800 rounded-xl">
                  <ListMusic size={56} className="mx-auto text-gray-700 mb-4" />
                  <h3 className="text-xl font-bold text-gray-400 mb-2">No Playlists Yet</h3>
                  <p className="text-gray-500 mb-5">Create a playlist to group your beats and share them</p>
                  <button onClick={openCreatePlaylist} className="px-6 py-3 bg-white text-black rounded-lg font-semibold flex items-center gap-2 mx-auto hover:bg-gray-200 transition-colors">
                    <Plus size={16} /> Create Your First Playlist
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {playlists.map(pl => (
                    <div key={pl.id} data-testid={`playlist-card-${pl.id}`} className="bg-gray-900/50 border border-gray-800 hover:border-gray-600 rounded-xl p-4 transition-all group">
                      {/* Playlist cover */}
                      <div className="w-full aspect-square rounded-lg overflow-hidden bg-gray-800 mb-3 grid grid-cols-2 grid-rows-2">
                        {[0,1,2,3].map(i => (
                          <div key={i} className="bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                            <Music size={14} className="text-gray-600" />
                          </div>
                        ))}
                      </div>
                      <h3 className="font-bold text-white truncate">{pl.name}</h3>
                      {pl.description && <p className="text-sm text-gray-500 truncate mt-0.5">{pl.description}</p>}
                      <p className="text-xs text-gray-600 mt-1">{pl.track_count || 0} track{(pl.track_count || 0) !== 1 ? 's' : ''}</p>

                      <div className="flex items-center gap-2 mt-3">
                        <button
                          data-testid={`share-playlist-${pl.id}`}
                          onClick={() => copyPlaylistLink(pl.id)}
                          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/15 rounded-lg text-xs font-medium transition-colors"
                        >
                          {playlistLinkCopied === pl.id ? <Check size={13} className="text-green-400" /> : <Share2 size={13} />}
                          {playlistLinkCopied === pl.id ? 'Copied!' : 'Share Link'}
                        </button>
                        <button
                          data-testid={`edit-playlist-${pl.id}`}
                          onClick={() => openEditPlaylist(pl)}
                          className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        ><Edit size={15} /></button>
                        <button
                          data-testid={`delete-playlist-${pl.id}`}
                          onClick={() => handleDeletePlaylist(pl.id)}
                          className="p-2 text-gray-600 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                        ><Trash2 size={15} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      )}

      {/* Video Detail Modal with Playback */}
      {isModalOpen && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-2 md:p-4">
          <div className="relative w-full max-w-5xl">
            <button
              onClick={closeVideoModal}
              className="absolute -top-10 md:-top-12 right-0 z-50 p-2 md:p-3 bg-black border-2 border-gray-400 rounded-lg hover:border-white transition-all"
            >
              <X size={20} className="text-white" />
            </button>

            <div className="w-full bg-black border-2 border-gray-400 rounded-xl overflow-hidden max-h-[90vh] overflow-y-auto">
              {/* Video Player */}
              <div className="relative aspect-video bg-gray-950">
                {selectedVideo.is_youtube && selectedVideo.youtube_id ? (
                  // YouTube Embed Player
                  <iframe
                    src={`https://www.youtube.com/embed/${selectedVideo.youtube_id}?autoplay=0&rel=0`}
                    title={selectedVideo.title || selectedVideo.name}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : getVideoUrl(selectedVideo) ? (
                  <>
                    <video
                      ref={videoPlayerRef}
                      src={getVideoUrl(selectedVideo)}
                      poster={getThumbnailUrl(selectedVideo)}
                      className="w-full h-full object-contain bg-black"
                      onClick={togglePlay}
                      onEnded={() => setIsPlaying(false)}
                    />
                    {/* Play overlay when paused */}
                    {!isPlaying && (
                      <div 
                        className="absolute inset-0 flex items-center justify-center cursor-pointer"
                        onClick={togglePlay}
                      >
                        <div className="w-16 md:w-20 h-16 md:h-20 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-all">
                          <Play size={28} className="text-black ml-1" />
                        </div>
                      </div>
                    )}
                    {/* Video Controls */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-3 md:p-4">
                      <div className="flex items-center gap-2 md:gap-3">
                        <button onClick={togglePlay} className="text-white hover:text-blue-400 transition-colors">
                          {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                        </button>
                        <button onClick={toggleMute} className="text-white hover:text-blue-400 transition-colors">
                          {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                        </button>
                        <div className="flex-1 h-1 bg-gray-600 rounded-full cursor-pointer">
                          <div className="h-full w-0 bg-blue-500 rounded-full"></div>
                        </div>
                        <span className="text-xs md:text-sm text-white">{selectedVideo.duration || '0:00'}</span>
                        <button onClick={toggleFullscreen} className="text-white hover:text-blue-400 transition-colors hidden sm:block">
                          <Maximize2 size={18} />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <img
                    src={getThumbnailUrl(selectedVideo)}
                    alt={selectedVideo.title || selectedVideo.name}
                    className="w-full h-full object-cover"
                    onError={handleImageError}
                  />
                )}
              </div>

              {/* Video Details */}
              <div className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row items-start justify-between gap-3 mb-4">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg md:text-2xl font-bold mb-2 truncate">{selectedVideo.title || selectedVideo.name}</h2>
                    <div className="flex items-center gap-2 md:gap-4 text-gray-400 flex-wrap">
                      <span className={`px-2 md:px-3 py-0.5 md:py-1 ${categoryColors[selectedVideo.category] || 'bg-purple-600'} rounded text-white text-xs md:text-sm`}>
                        {selectedVideo.category}
                      </span>
                      {selectedVideo.is_youtube && (
                        <span className="px-2 md:px-3 py-0.5 md:py-1 bg-red-600 rounded text-white text-xs md:text-sm flex items-center gap-1">
                          <Youtube size={12} /> YouTube
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-xs md:text-sm"><Eye size={14} /> {selectedVideo.views}</span>
                      <span className="flex items-center gap-1 text-xs md:text-sm"><Heart size={14} /> {selectedVideo.likes}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => openEditModal(selectedVideo, e)}
                    className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-sm"
                  >
                    <Edit size={16} />
                    <span className="hidden sm:inline">Edit Video</span>
                    <span className="sm:hidden">Edit</span>
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-t border-gray-700 pt-4 gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 md:w-12 h-10 md:h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-base md:text-lg font-bold">
                      {(selectedVideo.agent || 'Y').charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-sm md:text-base">{selectedVideo.agent || 'You'}</div>
                      <div className="text-xs md:text-sm text-gray-400">Uploaded by</div>
                    </div>
                  </div>
                  <div className="text-xs md:text-sm text-gray-400">
                    {selectedVideo.is_youtube ? (
                      <a href={selectedVideo.youtube_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline flex items-center gap-1">
                        <Link size={14} /> View on YouTube
                      </a>
                    ) : (
                      `Size: ${selectedVideo.size || 'Unknown'}`
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Video Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-2 md:p-4 z-50" onClick={() => resetUploadModal()}>
          <div className="bg-black border-2 border-gray-400 rounded-xl max-w-lg w-full p-4 md:p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
                <Video className="text-blue-400" size={20} />
                Add New Video
              </h2>
              <button onClick={() => resetUploadModal()} className="text-gray-400 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Upload Source Selection */}
            <div className="mb-4 md:mb-6">
              <label className="block text-xs md:text-sm font-semibold text-gray-400 mb-2 md:mb-3">Video Source</label>
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                <button
                  onClick={() => setUploadSource('file')}
                  className={`p-2.5 md:p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-1.5 md:gap-2 ${
                    uploadSource === 'file' ? 'border-blue-500 bg-blue-500/10 text-blue-400' : 'border-gray-600 hover:border-gray-500 text-gray-400'
                  }`}
                >
                  <Upload size={18} />
                  <span className="font-semibold text-xs md:text-sm">Upload File</span>
                </button>
                <button
                  onClick={() => setUploadSource('youtube')}
                  className={`p-2.5 md:p-3 rounded-xl border-2 transition-all flex items-center justify-center gap-1.5 md:gap-2 ${
                    uploadSource === 'youtube' ? 'border-red-500 bg-red-500/10 text-red-400' : 'border-gray-600 hover:border-gray-500 text-gray-400'
                  }`}
                >
                  <Youtube size={18} />
                  <span className="font-semibold text-xs md:text-sm">YouTube</span>
                </button>
              </div>
            </div>

            {/* Video Type Selection */}
            <div className="mb-4 md:mb-6">
              <label className="block text-xs md:text-sm font-semibold text-gray-400 mb-2 md:mb-3">Video Type</label>
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                <button
                  onClick={() => setUploadType('story')}
                  className={`p-3 md:p-4 rounded-xl border-2 transition-all ${
                    uploadType === 'story' ? 'border-blue-500 bg-blue-500/10' : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="flex flex-col items-center gap-1.5 md:gap-2">
                    <div className="w-10 md:w-12 h-16 md:h-20 bg-gray-800 rounded-lg flex items-center justify-center">
                      <Film size={20} className={uploadType === 'story' ? 'text-blue-400' : 'text-gray-400'} />
                    </div>
                    <span className={`font-semibold text-xs md:text-sm ${uploadType === 'story' ? 'text-blue-400' : 'text-white'}`}>Story</span>
                    <span className="text-[10px] md:text-xs text-gray-400">Vertical (9:16)</span>
                  </div>
                </button>
                <button
                  onClick={() => setUploadType('library')}
                  className={`p-3 md:p-4 rounded-xl border-2 transition-all ${
                    uploadType === 'library' ? 'border-purple-500 bg-purple-500/10' : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="flex flex-col items-center gap-1.5 md:gap-2">
                    <div className="w-16 md:w-20 h-10 md:h-12 bg-gray-800 rounded-lg flex items-center justify-center">
                      <Film size={20} className={uploadType === 'library' ? 'text-purple-400' : 'text-gray-400'} />
                    </div>
                    <span className={`font-semibold text-xs md:text-sm ${uploadType === 'library' ? 'text-purple-400' : 'text-white'}`}>Library</span>
                    <span className="text-[10px] md:text-xs text-gray-400">Horizontal (16:9)</span>
                  </div>
                </button>
              </div>
            </div>

            {/* YouTube URL Input */}
            {uploadSource === 'youtube' && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-400 mb-2">YouTube URL *</label>
                <div className="relative">
                  <Youtube className="absolute left-3 top-1/2 transform -translate-y-1/2 text-red-500" size={20} />
                  <input
                    type="text"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors"
                  />
                </div>
                {youtubeUrl && extractYoutubeId(youtubeUrl) && (
                  <div className="mt-3 rounded-lg overflow-hidden">
                    <img 
                      src={`https://img.youtube.com/vi/${extractYoutubeId(youtubeUrl)}/mqdefault.jpg`}
                      alt="YouTube thumbnail preview"
                      className="w-full h-auto"
                    />
                    <p className="text-xs text-green-400 mt-2 flex items-center gap-1">
                      <span>✓</span> Valid YouTube URL detected
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Video Upload Area - Only show for file upload */}
            {uploadSource === 'file' && (
              <div 
                className="border-2 border-dashed border-gray-600 rounded-xl p-6 mb-4 text-center hover:border-blue-500 transition-colors cursor-pointer"
                onClick={() => videoInputRef.current?.click()}
              >
                <input
                  ref={videoInputRef}
                  type="file"
                  onChange={handleVideoSelect}
                  className="hidden"
                  accept="video/*"
                />
                {selectedVideoFile ? (
                  <>
                    <Video className="mx-auto text-green-400 mb-3" size={48} />
                    <p className="text-white font-semibold mb-1">{selectedVideoFile.name}</p>
                    <p className="text-gray-400 text-sm mb-3">{(selectedVideoFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                    <button 
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                      onClick={(e) => { e.stopPropagation(); setSelectedVideoFile(null); setGeneratedThumbnail(null); if (videoInputRef.current) videoInputRef.current.value = ''; }}
                    >
                      Change Video
                    </button>
                  </>
                ) : (
                  <>
                    <Upload className="mx-auto text-gray-400 mb-3" size={48} />
                    <p className="text-white font-semibold mb-1">Drag and drop video here</p>
                    <p className="text-gray-400 text-sm mb-3">or click to browse</p>
                    <button className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors">
                      Browse Files
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Thumbnail Preview & Custom Upload - Only for file uploads */}
            {uploadSource === 'file' && (generatedThumbnail || selectedThumbnailFile) && (
              <div className="mb-4 p-4 bg-gray-900 rounded-xl">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-gray-400">Thumbnail Preview</span>
                  <button
                    onClick={() => thumbnailInputRef.current?.click()}
                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  >
                    <Image size={14} /> Change Thumbnail
                  </button>
                </div>
                <div className="relative aspect-video w-full max-w-xs mx-auto rounded-lg overflow-hidden bg-gray-800">
                  <img
                    src={selectedThumbnailFile ? URL.createObjectURL(selectedThumbnailFile) : (generatedThumbnail ? URL.createObjectURL(generatedThumbnail) : '')}
                    alt="Thumbnail preview"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-xs text-gray-500 text-center mt-2">
                  {selectedThumbnailFile ? 'Custom thumbnail' : 'Auto-generated from video'}
                </p>
              </div>
            )}

            {/* Custom Thumbnail Upload (hidden input) */}
            <input
              ref={thumbnailInputRef}
              type="file"
              onChange={handleThumbnailSelect}
              className="hidden"
              accept="image/*"
            />

            {/* Or upload custom thumbnail button (shown when no video selected yet) - Only for file uploads */}
            {uploadSource === 'file' && !generatedThumbnail && !selectedThumbnailFile && (
              <div className="mb-4">
                <button
                  onClick={() => thumbnailInputRef.current?.click()}
                  className="w-full py-3 border-2 border-dashed border-gray-600 rounded-xl text-gray-400 hover:border-gray-500 hover:text-gray-300 transition-colors flex items-center justify-center gap-2"
                >
                  <Image size={20} />
                  Upload Custom Thumbnail (Optional)
                </button>
              </div>
            )}

            {/* Video Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Video Title *</label>
                <input
                  type="text"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  placeholder="Enter video title"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Category</label>
                <select
                  value={videoCategory}
                  onChange={(e) => setVideoCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
                >
                  {categories.filter(c => c !== 'All').map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => resetUploadModal()}
                className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadVideo}
                disabled={isUploading || (uploadSource === 'file' ? !selectedVideoFile : !youtubeUrl) || !videoTitle}
                className={`px-6 py-2.5 text-white rounded-lg transition-all font-semibold flex items-center gap-2 ${
                  uploadSource === 'youtube'
                    ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                    : uploadType === 'story'
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600'
                    : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600'
                } disabled:from-gray-600 disabled:to-gray-600`}
              >
                {isUploading ? (
                  <><Loader2 size={18} className="animate-spin" />{uploadSource === 'youtube' ? 'Adding...' : 'Uploading...'}</>
                ) : uploadSource === 'youtube' ? (
                  <>
                    <Youtube size={18} />
                    Add YouTube Video
                  </>
                ) : (
                  `Upload ${uploadType === 'story' ? 'Story' : 'to Library'}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Video Modal */}
      {showEditModal && editingVideo && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" onClick={() => setShowEditModal(false)}>
          <div className="bg-black border-2 border-gray-400 rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Edit className="text-blue-400" size={24} />
                Edit Video
              </h2>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>

            {/* YouTube URL - Show if it's a YouTube video or to add YouTube URL */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-400 mb-2">
                YouTube URL {editingVideo.is_youtube && <span className="text-red-400">(YouTube Video)</span>}
              </label>
              <div className="relative">
                <Youtube className="absolute left-3 top-1/2 transform -translate-y-1/2 text-red-500" size={20} />
                <input
                  type="text"
                  value={editYoutubeUrl}
                  onChange={(e) => setEditYoutubeUrl(e.target.value)}
                  placeholder={editingVideo.is_youtube ? "Update YouTube URL" : "Add YouTube URL (converts to YouTube video)"}
                  className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors"
                />
              </div>
              {editYoutubeUrl && extractYoutubeId(editYoutubeUrl) && editYoutubeUrl !== editingVideo.youtube_url && (
                <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                  <span>✓</span> Valid YouTube URL - will update on save
                </p>
              )}
            </div>

            {/* Current Thumbnail */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-400 mb-2">Thumbnail</label>
              <div className="relative aspect-video w-full rounded-lg overflow-hidden bg-gray-800 mb-2">
                <img
                  src={newThumbnailFile ? URL.createObjectURL(newThumbnailFile) : getThumbnailUrl(editingVideo)}
                  alt="Current thumbnail"
                  className="w-full h-full object-cover"
                />
              </div>
              <input
                ref={editThumbnailRef}
                type="file"
                onChange={(e) => setNewThumbnailFile(e.target.files[0])}
                className="hidden"
                accept="image/*"
              />
              <button
                onClick={() => editThumbnailRef.current?.click()}
                className="w-full py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 flex items-center justify-center gap-2 transition-colors"
              >
                <Image size={16} />
                {newThumbnailFile ? 'Change Thumbnail' : 'Upload New Thumbnail'}
              </button>
            </div>

            {/* Replace Video File - Only show for non-YouTube videos */}
            {!editingVideo.is_youtube && (
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-400 mb-2">Video File</label>
                <input
                  ref={editVideoRef}
                  type="file"
                  onChange={(e) => setNewVideoFile(e.target.files[0])}
                  className="hidden"
                  accept="video/*"
                />
                <button
                  onClick={() => editVideoRef.current?.click()}
                  className="w-full py-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-gray-500 hover:text-gray-300 transition-colors flex items-center justify-center gap-2"
                >
                  <RefreshCw size={18} />
                  {newVideoFile ? newVideoFile.name : 'Replace Video File (Optional)'}
                </button>
              </div>
            )}

            {/* Edit Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Video Title</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Enter video title"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Category</label>
                <select
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
                >
                  {categories.filter(c => c !== 'All').map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Video Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setEditType('story')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      editType === 'story' ? 'border-blue-500 bg-blue-500/10 text-blue-400' : 'border-gray-600 text-gray-400'
                    }`}
                  >
                    Story (9:16)
                  </button>
                  <button
                    onClick={() => setEditType('library')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      editType === 'library' ? 'border-purple-500 bg-purple-500/10 text-purple-400' : 'border-gray-600 text-gray-400'
                    }`}
                  >
                    Library (16:9)
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between gap-3 mt-6">
              <button
                onClick={() => handleDeleteVideo(editingVideo.id, editingVideo.video_type || editingVideo.type)}
                className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Trash2 size={18} />
                Delete
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateVideo}
                  disabled={isUpdating}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-semibold flex items-center gap-2"
                >
                  {isUpdating ? (
                    <><Loader2 size={18} className="animate-spin" />Saving...</>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Media Category Management Modal */}
      {showMediaCategoryModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowMediaCategoryModal(false)}>
          <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Edit Media Categories</h3>
              <button onClick={() => setShowMediaCategoryModal(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={18} className="text-gray-400" /></button>
            </div>
            <div className="p-4 border-b border-gray-700">
              <div className="flex gap-2 mb-3">
                <div className="relative">
                  <button className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center" onClick={(e) => e.currentTarget.nextElementSibling.classList.toggle('hidden')}>{newMediaCategoryIcon}</button>
                  <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-5 gap-1 w-40">
                    {mediaCategoryIcons.map(icon => (<button key={icon} onClick={(e) => { setNewMediaCategoryIcon(icon); e.currentTarget.parentElement.classList.add('hidden'); }} className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center">{icon}</button>))}
                  </div>
                </div>
                <input type="text" value={newMediaCategoryName} onChange={(e) => setNewMediaCategoryName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addMediaCategory()} placeholder="New category name..." className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" data-testid="new-media-category-input" />
              </div>
              <div className="flex gap-2 items-center mb-3">
                <span className="text-xs text-gray-400">Color:</span>
                <div className="flex gap-1 flex-wrap">
                  {mediaCategoryColors.map(color => (<button key={color} onClick={() => setNewMediaCategoryColor(color)} className={`w-6 h-6 rounded-full ${color} ${newMediaCategoryColor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : ''}`} />))}
                </div>
              </div>
              <button onClick={addMediaCategory} disabled={!newMediaCategoryName.trim()} className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5" data-testid="add-media-category-btn"><Plus size={14} />Add Category</button>
            </div>
            <div className="p-4 max-h-80 overflow-y-auto space-y-2">
              {customMediaCategories.filter(c => c.id !== 'All').map(cat => (
                <div key={cat.id} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group">
                  {editingMediaCategory === cat.id ? (
                    <>
                      <span className="text-lg">{cat.icon}</span>
                      <input type="text" value={editMediaCategoryValue} onChange={(e) => setEditMediaCategoryValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') updateMediaCategory(cat.id, editMediaCategoryValue); if (e.key === 'Escape') { setEditingMediaCategory(null); setEditMediaCategoryValue(''); }}} className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm" autoFocus />
                      <button onClick={() => updateMediaCategory(cat.id, editMediaCategoryValue)} className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"><Check size={14} /></button>
                      <button onClick={() => { setEditingMediaCategory(null); setEditMediaCategoryValue(''); }} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <span className={`w-8 h-8 rounded-lg ${cat.color} flex items-center justify-center text-sm`}>{cat.icon}</span>
                      <span className="flex-1 text-white text-sm">{cat.name}{cat.id === 'Highlights' && <span className="ml-2 text-xs text-gray-500">(default)</span>}</span>
                      <button onClick={() => { setEditingMediaCategory(cat.id); setEditMediaCategoryValue(cat.name); }} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100"><Pencil size={14} /></button>
                      {cat.id !== 'Highlights' && <button onClick={() => deleteMediaCategory(cat.id)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-700">
              <button onClick={() => setShowMediaCategoryModal(false)} className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium" data-testid="done-media-category-btn">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Audio Upload Modal */}
      {showAudioUploadModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" onClick={() => resetAudioUploadModal()}>
          <div className="bg-black border-2 border-gray-600 rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Music className="text-purple-400" size={24} />
                Upload Track
              </h2>
              <button onClick={() => resetAudioUploadModal()} className="text-gray-400 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>

            {/* Audio File Upload */}
            <div 
              className="border-2 border-dashed border-gray-600 rounded-xl p-6 mb-4 text-center hover:border-purple-500 transition-colors cursor-pointer"
              onClick={() => audioInputRef.current?.click()}
            >
              <input
                ref={audioInputRef}
                type="file"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setSelectedAudioFile(file);
                    if (!audioTitle) {
                      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
                      setAudioTitle(nameWithoutExt);
                    }
                  }
                }}
                className="hidden"
                accept=".mp3,.wav,.m4a,.aac,.ogg,.flac"
              />
              {selectedAudioFile ? (
                <>
                  <Music className="mx-auto text-purple-400 mb-3" size={48} />
                  <p className="text-white font-semibold mb-1">{selectedAudioFile.name}</p>
                  <p className="text-gray-400 text-sm">{(selectedAudioFile.size / (1024 * 1024)).toFixed(1)} MB</p>
                </>
              ) : (
                <>
                  <Upload className="mx-auto text-gray-400 mb-3" size={48} />
                  <p className="text-white font-semibold mb-1">Drop audio file here</p>
                  <p className="text-gray-400 text-sm">MP3, WAV, M4A, FLAC supported</p>
                </>
              )}
            </div>

            {/* Cover Art Upload with AI Generation */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-400 mb-2">Cover Art (Optional)</label>
              
              {/* Preview if we have a generated or selected cover */}
              {(generatedUploadCoverPreview || selectedCoverFile) && (
                <div className="mb-3 flex items-center gap-4 p-3 bg-gray-900 rounded-lg">
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                    {generatedUploadCoverPreview ? (
                      <img src={generatedUploadCoverPreview} alt="AI Generated Cover" className="w-full h-full object-cover" />
                    ) : selectedCoverFile ? (
                      <img src={URL.createObjectURL(selectedCoverFile)} alt="Cover Preview" className="w-full h-full object-cover" />
                    ) : null}
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">
                      {generatedUploadCoverPreview ? 'AI Generated Cover' : selectedCoverFile?.name}
                    </p>
                    <button
                      onClick={() => {
                        setSelectedCoverFile(null);
                        setGeneratedUploadCoverPreview(null);
                        if (coverInputRef.current) coverInputRef.current.value = '';
                      }}
                      className="text-red-400 text-xs hover:text-red-300 mt-1"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
              
              <input
                ref={coverInputRef}
                type="file"
                onChange={(e) => {
                  setSelectedCoverFile(e.target.files[0]);
                  setGeneratedUploadCoverPreview(null);
                }}
                className="hidden"
                accept=".jpg,.jpeg,.png,.webp"
              />
              
              <div className="flex gap-2">
                <button
                  onClick={() => coverInputRef.current?.click()}
                  className="flex-1 py-3 border border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-gray-500 hover:text-gray-300 transition-colors flex items-center justify-center gap-2"
                >
                  <Upload size={18} />
                  Upload Image
                </button>
                <button
                  onClick={generateUploadCover}
                  disabled={generatingUploadCover || !audioTitle}
                  className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors flex items-center justify-center gap-2"
                  title={!audioTitle ? 'Enter a track title first' : 'Generate AI cover art based on track details'}
                >
                  {generatingUploadCover ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Wand2 size={18} />
                  )}
                  {generatingUploadCover ? 'Generating...' : 'AI Generate'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">AI generates cover art based on title, genre, mood & other track details</p>
            </div>

            {/* Track Details */}
            <div className="space-y-4">
              {/* Title & Artist */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Title *</label>
                  <input
                    type="text"
                    value={audioTitle}
                    onChange={(e) => setAudioTitle(e.target.value)}
                    placeholder="Enter track title"
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Artist / Producer</label>
                  <input
                    type="text"
                    value={audioArtist}
                    onChange={(e) => setAudioArtist(e.target.value)}
                    placeholder="Your name or producer tag"
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {/* BPM with Detect button */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">BPM</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={audioBpm}
                      onChange={(e) => setAudioBpm(e.target.value)}
                      placeholder="120"
                      className="flex-1 min-w-0 px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                    />
                    <button
                      onClick={() => detectUploadBpm(selectedAudioFile)}
                      disabled={detectingUploadBpm || !selectedAudioFile}
                      data-testid="detect-bpm-btn"
                      className="flex-shrink-0 px-3 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      title="Auto-detect BPM"
                    >
                      {detectingUploadBpm ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Activity size={16} />
                      )}
                      <span className="hidden sm:inline">{detectingUploadBpm ? 'Detecting...' : 'Detect'}</span>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Key</label>
                  <div className="flex gap-2">
                    <select
                      value={audioKey}
                      onChange={(e) => setAudioKey(e.target.value)}
                      className="flex-1 min-w-0 px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    >
                      <option value="">Select Key</option>
                      {filterOptions.key.map((key) => (
                        <option key={key} value={key}>{key}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => detectUploadKey(selectedAudioFile)}
                      disabled={detectingUploadKey || !selectedAudioFile}
                      data-testid="detect-key-btn"
                      className="flex-shrink-0 px-3 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      title="Auto-detect Key"
                    >
                      {detectingUploadKey ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Music size={16} />
                      )}
                      <span className="hidden sm:inline">{detectingUploadKey ? 'Detecting...' : 'Detect'}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Genre & Track Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Genre</label>
                  <select
                    value={audioGenre}
                    onChange={(e) => setAudioGenre(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="">Select Genre</option>
                    {filterOptions.genre.map((genre) => (
                      <option key={genre} value={genre}>{genre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Track Type</label>
                  <select
                    value={audioTrackType}
                    onChange={(e) => setAudioTrackType(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="">Select Type</option>
                    {filterOptions.trackType.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Mood & Instrument */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Mood</label>
                  <select
                    value={audioMood}
                    onChange={(e) => setAudioMood(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="">Select Mood</option>
                    {filterOptions.mood.map((mood) => (
                      <option key={mood} value={mood}>{mood}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Instrument</label>
                  <select
                    value={audioInstrument}
                    onChange={(e) => setAudioInstrument(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="">Select Instrument</option>
                    {filterOptions.instrument.map((instrument) => (
                      <option key={instrument} value={instrument}>{instrument}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Category</label>
                <select
                  value={audioCategory}
                  onChange={(e) => setAudioCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="Beats">Beats</option>
                  <option value="Instrumentals">Instrumentals</option>
                  <option value="Samples">Samples</option>
                  <option value="Loops">Loops</option>
                  <option value="Vocals">Vocals</option>
                </select>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Tags (comma separated)</label>
                <input
                  type="text"
                  value={audioTags}
                  onChange={(e) => setAudioTags(e.target.value)}
                  placeholder="trap, dark, 6lack type beat"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Published Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Published Date</label>
                <input
                  type="date"
                  value={audioPublishedDate}
                  onChange={(e) => setAudioPublishedDate(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Pricing */}
              <div className="flex items-center justify-between p-4 bg-gray-900 rounded-lg">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="audioIsFree"
                    checked={audioIsFree}
                    onChange={(e) => setAudioIsFree(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500"
                  />
                  <label htmlFor="audioIsFree" className="text-white font-medium">Free Download</label>
                </div>
                {!audioIsFree && (
                  <div className="flex items-center gap-2">
                    <DollarSign size={18} className="text-gray-400" />
                    <input
                      type="number"
                      value={audioPrice}
                      onChange={(e) => setAudioPrice(e.target.value)}
                      placeholder="29.99"
                      className="w-24 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 text-sm"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => resetAudioUploadModal()}
                className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                disabled={isUploadingAudio}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadAudio}
                disabled={isUploadingAudio || !selectedAudioFile || !audioTitle}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploadingAudio ? (
                  <><Loader2 size={18} className="animate-spin" /> Uploading...</>
                ) : (
                  <><Upload size={18} /> Upload Track</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" onClick={() => !isBulkUploading && setShowBulkUploadModal(false)}>
          <div className="bg-black border-2 border-gray-600 rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Upload className="text-purple-400" size={24} />
                Bulk Upload Tracks
              </h2>
              <button onClick={() => !isBulkUploading && setShowBulkUploadModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            {/* File Drop Zone */}
            <div
              className="border-2 border-dashed border-gray-600 rounded-xl p-8 mb-4 text-center hover:border-purple-500 transition-colors cursor-pointer"
              onClick={() => bulkUploadInputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => {
                e.preventDefault();
                const newFiles = Array.from(e.dataTransfer.files)
                  .filter(f => ['.mp3','.wav','.m4a','.aac','.ogg','.flac'].some(ext => f.name.toLowerCase().endsWith(ext)));
                setBulkUploadFiles(prev => [...prev, ...newFiles.map(file => ({ file, status: 'pending' }))]);
              }}
            >
              <input
                ref={bulkUploadInputRef}
                type="file"
                multiple
                accept=".mp3,.wav,.m4a,.aac,.ogg,.flac"
                className="hidden"
                onChange={e => {
                  const newFiles = Array.from(e.target.files);
                  setBulkUploadFiles(prev => [...prev, ...newFiles.map(file => ({ file, status: 'pending' }))]);
                  e.target.value = '';
                }}
              />
              <Upload size={32} className="mx-auto text-gray-500 mb-2" />
              <p className="text-gray-400 font-medium">Drop audio files here or click to select</p>
              <p className="text-gray-600 text-sm mt-1">MP3, WAV, M4A, AAC, OGG, FLAC</p>
            </div>

            {/* File Queue */}
            {bulkUploadFiles.length > 0 && (
              <div className="mb-4 space-y-1 max-h-48 overflow-y-auto">
                {bulkUploadFiles.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2 bg-gray-900 rounded-lg text-sm">
                    <Music size={14} className="text-gray-500 flex-shrink-0" />
                    <span className="flex-1 truncate text-gray-300">{item.file.name}</span>
                    {item.status === 'pending' && <span className="text-gray-500 text-xs">Queued</span>}
                    {item.status === 'uploading' && <Loader2 size={14} className="animate-spin text-purple-400" />}
                    {item.status === 'done' && <Check size={14} className="text-green-400" />}
                    {item.status === 'error' && <span className="text-red-400 text-xs">{item.error}</span>}
                    {item.status === 'pending' && !isBulkUploading && (
                      <button onClick={() => setBulkUploadFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-gray-600 hover:text-red-400 ml-1">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Common Metadata */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Artist (applied to all)</label>
                <input value={bulkArtist} onChange={e => setBulkArtist(e.target.value)} placeholder="Artist name"
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Genre</label>
                <select value={bulkGenre} onChange={e => setBulkGenre(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500">
                  <option value="">Select genre</option>
                  {filterOptions.genre.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Mood</label>
                <select value={bulkMood} onChange={e => setBulkMood(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500">
                  <option value="">Select mood</option>
                  {filterOptions.mood.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Category</label>
                <select value={bulkCategory} onChange={e => setBulkCategory(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500">
                  {['Beats','Instrumentals','Samples','Loops','Vocals','Sound Effects'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-3 col-span-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={bulkIsFree} onChange={e => setBulkIsFree(e.target.checked)} className="w-4 h-4 accent-white" />
                  <span className="text-sm text-gray-300">Free tracks</span>
                </label>
                {!bulkIsFree && (
                  <input value={bulkPrice} onChange={e => setBulkPrice(e.target.value)} placeholder="Price ($)" type="number" min="0" step="0.01"
                    className="w-32 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500" />
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => !isBulkUploading && setShowBulkUploadModal(false)}
                className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors" disabled={isBulkUploading}>
                {bulkUploadFiles.every(f => f.status === 'done') ? 'Close' : 'Cancel'}
              </button>
              <button
                data-testid="bulk-upload-start-btn"
                onClick={handleBulkUpload}
                disabled={isBulkUploading || bulkUploadFiles.length === 0 || bulkUploadFiles.every(f => f.status === 'done')}
                className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isBulkUploading ? <><Loader2 size={18} className="animate-spin" /> Uploading...</> : `Upload ${bulkUploadFiles.filter(f => f.status !== 'done').length} Tracks`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" onClick={() => setShowBulkEditModal(false)}>
          <div className="bg-black border-2 border-gray-600 rounded-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Edit className="text-blue-400" size={20} />
                Edit {selectedTrackIds.size} Track{selectedTrackIds.size > 1 ? 's' : ''}
              </h2>
              <button onClick={() => setShowBulkEditModal(false)} className="text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <p className="text-sm text-gray-500 mb-4">Only filled fields will be updated. Leave blank to keep existing values.</p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Artist</label>
                <input
                  value={bulkEditArtist}
                  onChange={e => setBulkEditArtist(e.target.value)}
                  placeholder="— Keep existing —"
                  data-testid="bulk-edit-artist-input"
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Genre</label>
                <select value={bulkEditGenre} onChange={e => setBulkEditGenre(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500">
                  <option value="">— Keep existing —</option>
                  {filterOptions.genre.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Mood</label>
                <select value={bulkEditMood} onChange={e => setBulkEditMood(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500">
                  <option value="">— Keep existing —</option>
                  {filterOptions.mood.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Category</label>
                <select value={bulkEditCategory} onChange={e => setBulkEditCategory(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500">
                  <option value="">— Keep existing —</option>
                  {['Beats','Instrumentals','Samples','Loops','Vocals','Sound Effects'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Free / Paid</label>
                <select value={bulkEditIsFree} onChange={e => setBulkEditIsFree(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500">
                  <option value="">— Keep existing —</option>
                  <option value="true">Free</option>
                  <option value="false">Paid</option>
                </select>
              </div>
              {bulkEditIsFree === 'false' && (
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Price ($)</label>
                  <input value={bulkEditPrice} onChange={e => setBulkEditPrice(e.target.value)} type="number" min="0" step="0.01" placeholder="0.00"
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500" />
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-400 mb-1">Replace Tags (comma-separated)</label>
                <input value={bulkEditTags} onChange={e => setBulkEditTags(e.target.value)} placeholder="tag1, tag2, tag3"
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-gray-500" />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowBulkEditModal(false)} className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors">Cancel</button>
              <button
                data-testid="bulk-edit-apply-btn"
                onClick={handleBulkEdit}
                disabled={isBulkEditing}
                className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isBulkEditing ? <><Loader2 size={16} className="animate-spin" /> Applying...</> : `Apply to ${selectedTrackIds.size} Track${selectedTrackIds.size > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Playlist Modal */}
      {showCreatePlaylistModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" onClick={() => setShowCreatePlaylistModal(false)}>
          <div className="bg-black border-2 border-gray-700 rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2"><ListMusic size={20} className="text-white" />New Playlist</h2>
              <button onClick={() => setShowCreatePlaylistModal(false)} className="text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Playlist Name *</label>
                <input value={playlistName} onChange={e => setPlaylistName(e.target.value)} placeholder="e.g. Dark Trap Vibes"
                  className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gray-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Description (optional)</label>
                <textarea value={playlistDescription} onChange={e => setPlaylistDescription(e.target.value)} placeholder="What's this playlist about?"
                  rows={2} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gray-500 resize-none text-sm" />
              </div>
            </div>

            {/* Track picker */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-400">Pick Tracks ({playlistSelectedTracks.length} selected)</label>
                {playlistSelectedTracks.length > 0 && (
                  <button onClick={() => setPlaylistSelectedTracks([])} className="text-xs text-gray-500 hover:text-white">Clear all</button>
                )}
              </div>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input value={playlistTrackSearch} onChange={e => setPlaylistTrackSearch(e.target.value)} placeholder="Search tracks..."
                  className="w-full pl-8 pr-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-gray-500" />
              </div>
              <div className="max-h-56 overflow-y-auto space-y-1 border border-gray-800 rounded-lg p-2">
                {audioTracks
                  .filter(t => !playlistTrackSearch || t.title?.toLowerCase().includes(playlistTrackSearch.toLowerCase()) || t.artist?.toLowerCase().includes(playlistTrackSearch.toLowerCase()))
                  .map(track => (
                    <label key={track.id} className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer">
                      <input type="checkbox" checked={playlistSelectedTracks.includes(track.id)} onChange={() => togglePlaylistTrack(track.id)} className="w-4 h-4 accent-white" />
                      <div className="w-9 h-9 rounded overflow-hidden flex-shrink-0 bg-gray-800">
                        {track.cover_url ? <FastImage src={`${API_URL}${track.cover_url}`} alt="" className="w-full h-full object-cover" fallbackIcon={Music} fallbackClassName="text-gray-600" /> : <div className="w-full h-full flex items-center justify-center"><Music size={14} className="text-gray-600" /></div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{track.title}</p>
                        <p className="text-xs text-gray-500 truncate">{track.artist}</p>
                      </div>
                      {track.duration && <span className="text-xs text-gray-600">{Math.floor(track.duration/60)}:{String(Math.floor(track.duration%60)).padStart(2,'0')}</span>}
                    </label>
                  ))}
                {audioTracks.length === 0 && <p className="text-center text-gray-600 text-sm py-4">No tracks uploaded yet</p>}
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowCreatePlaylistModal(false)} className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium">Cancel</button>
              <button
                data-testid="save-playlist-btn"
                onClick={() => handleSavePlaylist(false)}
                disabled={isSavingPlaylist || !playlistName.trim()}
                className="flex-1 py-3 bg-white text-black rounded-lg font-bold disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-gray-200 transition-colors"
              >
                {isSavingPlaylist ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : 'Create Playlist'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Playlist Modal */}
      {showEditPlaylistModal && editingPlaylist && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" onClick={() => setShowEditPlaylistModal(false)}>
          <div className="bg-black border-2 border-gray-700 rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2"><Edit size={18} className="text-white" />Edit Playlist</h2>
              <button onClick={() => setShowEditPlaylistModal(false)} className="text-gray-400 hover:text-white"><X size={24} /></button>
            </div>
            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Playlist Name *</label>
                <input value={playlistName} onChange={e => setPlaylistName(e.target.value)}
                  className="w-full px-3 py-2.5 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gray-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Description</label>
                <textarea value={playlistDescription} onChange={e => setPlaylistDescription(e.target.value)} rows={2}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-gray-500 resize-none text-sm" />
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-400">Tracks ({playlistSelectedTracks.length} selected)</label>
                {playlistSelectedTracks.length > 0 && (
                  <button onClick={() => setPlaylistSelectedTracks([])} className="text-xs text-gray-500 hover:text-white">Clear all</button>
                )}
              </div>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input value={playlistTrackSearch} onChange={e => setPlaylistTrackSearch(e.target.value)} placeholder="Search tracks..."
                  className="w-full pl-8 pr-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-gray-500" />
              </div>
              <div className="max-h-56 overflow-y-auto space-y-1 border border-gray-800 rounded-lg p-2">
                {audioTracks
                  .filter(t => !playlistTrackSearch || t.title?.toLowerCase().includes(playlistTrackSearch.toLowerCase()) || t.artist?.toLowerCase().includes(playlistTrackSearch.toLowerCase()))
                  .map(track => (
                    <label key={track.id} className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer">
                      <input type="checkbox" checked={playlistSelectedTracks.includes(track.id)} onChange={() => togglePlaylistTrack(track.id)} className="w-4 h-4 accent-white" />
                      <div className="w-9 h-9 rounded overflow-hidden flex-shrink-0 bg-gray-800">
                        {track.cover_url ? <FastImage src={`${API_URL}${track.cover_url}`} alt="" className="w-full h-full object-cover" fallbackIcon={Music} fallbackClassName="text-gray-600" /> : <div className="w-full h-full flex items-center justify-center"><Music size={14} className="text-gray-600" /></div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{track.title}</p>
                        <p className="text-xs text-gray-500 truncate">{track.artist}</p>
                      </div>
                      {track.duration && <span className="text-xs text-gray-600">{Math.floor(track.duration/60)}:{String(Math.floor(track.duration%60)).padStart(2,'0')}</span>}
                    </label>
                  ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowEditPlaylistModal(false)} className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium">Cancel</button>
              <button
                data-testid="update-playlist-btn"
                onClick={() => handleSavePlaylist(true)}
                disabled={isSavingPlaylist || !playlistName.trim()}
                className="flex-1 py-3 bg-white text-black rounded-lg font-bold disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-gray-200 transition-colors"
              >
                {isSavingPlaylist ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audio Edit Modal */}
      {showAudioEditModal && editingTrack && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" onClick={() => setShowAudioEditModal(false)}>
          <div className="bg-black border-2 border-gray-600 rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Edit className="text-purple-400" size={24} />
                Edit Track
              </h2>
              <button onClick={() => setShowAudioEditModal(false)} className="text-gray-400 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>

            {/* Track Cover Preview - Editable with AI Generation */}
            <div className="mb-6 p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center gap-4 mb-3">
                <div 
                  className="relative w-24 h-24 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0 cursor-pointer group"
                  onClick={() => editCoverInputRef.current?.click()}
                >
                  {editCoverPreview ? (
                    <img src={editCoverPreview} alt="New cover" className="w-full h-full object-cover" />
                  ) : editingTrack.cover_url ? (
                    <img src={`${API_URL}${editingTrack.cover_url}`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-600 to-blue-600">
                      <Music size={32} className="text-white" />
                    </div>
                  )}
                  {/* Upload overlay */}
                  <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera size={20} className="text-white mb-1" />
                    <span className="text-white text-xs font-medium">Upload</span>
                  </div>
                </div>
                <input
                  ref={editCoverInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleEditCoverSelect}
                  className="hidden"
                />
                <div className="flex-1">
                  <h3 className="font-semibold text-white text-lg">{editingTrack.title}</h3>
                  <p className="text-gray-400">{editingTrack.artist}</p>
                  {editCoverFile && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-green-400 flex items-center gap-1">
                        <Check size={12} />
                        New cover selected
                      </span>
                      <button
                        onClick={() => { setEditCoverFile(null); setEditCoverPreview(null); }}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* AI Generate Cover Button */}
              <button
                onClick={generateEditCover}
                disabled={generatingEditCover}
                className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors flex items-center justify-center gap-2 text-sm"
              >
                {generatingEditCover ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Generating AI Cover...
                  </>
                ) : (
                  <>
                    <Wand2 size={16} />
                    Generate AI Cover Art
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">AI creates cover art based on track title, genre, mood & details</p>
            </div>

            {/* Replace Audio File Section */}
            <div className="mb-6 p-4 bg-gray-900 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                <Music size={16} className="text-purple-400" />
                Audio File
              </h4>
              
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-400 truncate">
                    Current: {editingTrack.original_filename || 'Unknown file'}
                  </p>
                  {editingTrack.duration && (
                    <p className="text-xs text-gray-500">
                      Duration: {Math.floor(editingTrack.duration / 60)}:{(editingTrack.duration % 60).toString().padStart(2, '0')}
                    </p>
                  )}
                </div>
                
                <button
                  onClick={() => editAudioInputRef.current?.click()}
                  disabled={uploadingEditAudio}
                  className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded-lg text-white text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {uploadingEditAudio ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <RefreshCw size={14} />
                      Replace File
                    </>
                  )}
                </button>
                
                <input
                  ref={editAudioInputRef}
                  type="file"
                  accept="audio/*"
                  onChange={handleEditAudioSelect}
                  className="hidden"
                />
              </div>
              
              {editAudioFileName && (
                <div className="mt-3 p-2 bg-green-900/30 border border-green-500/30 rounded-lg flex items-center justify-between">
                  <span className="text-sm text-green-400 flex items-center gap-2">
                    <Check size={14} />
                    New file: {editAudioFileName}
                  </span>
                  <button
                    onClick={() => { setEditAudioFile(null); setEditAudioFileName(null); }}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {/* Edit Form */}
            <div className="space-y-4">
              {/* Title & Artist */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Title *</label>
                  <input
                    type="text"
                    value={editTrackTitle}
                    onChange={(e) => setEditTrackTitle(e.target.value)}
                    placeholder="Track title"
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Artist / Producer</label>
                  <input
                    type="text"
                    value={editTrackArtist}
                    onChange={(e) => setEditTrackArtist(e.target.value)}
                    placeholder="Artist name"
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {/* BPM with Detection & Key - Separate buttons */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">BPM</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={editTrackBpm}
                      onChange={(e) => setEditTrackBpm(e.target.value)}
                      placeholder="120"
                      className="flex-1 min-w-0 px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                    />
                    <button
                      onClick={() => detectEditBpm(editingTrack.audio_url)}
                      disabled={detectingBpm}
                      className="flex-shrink-0 px-3 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 text-sm font-medium"
                      title="Auto-detect BPM"
                    >
                      {detectingBpm ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Activity size={16} />
                      )}
                      <span className="hidden sm:inline">{detectingBpm ? 'Detecting...' : 'Detect'}</span>
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Key</label>
                  <div className="flex gap-2">
                    <select
                      value={editTrackKey}
                      onChange={(e) => setEditTrackKey(e.target.value)}
                      className="flex-1 min-w-0 px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    >
                      <option value="">Select Key</option>
                      {filterOptions.key.map((key) => (
                        <option key={key} value={key}>{key}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => detectEditKey(editingTrack.audio_url)}
                      disabled={detectingKey}
                      className="flex-shrink-0 px-3 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 text-sm font-medium"
                      title="Auto-detect Key"
                    >
                      {detectingKey ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Music size={16} />
                      )}
                      <span className="hidden sm:inline">{detectingKey ? 'Detecting...' : 'Detect'}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Genre & Track Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Genre</label>
                  <select
                    value={editTrackGenre}
                    onChange={(e) => setEditTrackGenre(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="">Select Genre</option>
                    {filterOptions.genre.map((genre) => (
                      <option key={genre} value={genre}>{genre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Track Type</label>
                  <select
                    value={editTrackType}
                    onChange={(e) => setEditTrackType(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="">Select Type</option>
                    {filterOptions.trackType.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Mood & Instrument */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Mood</label>
                  <select
                    value={editTrackMood}
                    onChange={(e) => setEditTrackMood(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="">Select Mood</option>
                    {filterOptions.mood.map((mood) => (
                      <option key={mood} value={mood}>{mood}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-400 mb-2">Instrument</label>
                  <select
                    value={editTrackInstrument}
                    onChange={(e) => setEditTrackInstrument(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  >
                    <option value="">Select Instrument</option>
                    {filterOptions.instrument.map((instrument) => (
                      <option key={instrument} value={instrument}>{instrument}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Category</label>
                <select
                  value={editTrackCategory}
                  onChange={(e) => setEditTrackCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="Beats">Beats</option>
                  <option value="Instrumentals">Instrumentals</option>
                  <option value="Samples">Samples</option>
                  <option value="Loops">Loops</option>
                  <option value="Vocals">Vocals</option>
                </select>
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Tags (comma separated)</label>
                <input
                  type="text"
                  value={editTrackTags}
                  onChange={(e) => setEditTrackTags(e.target.value)}
                  placeholder="trap, dark, 6lack type beat"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Published Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Published Date</label>
                <input
                  type="date"
                  value={editTrackPublishedDate}
                  onChange={(e) => setEditTrackPublishedDate(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Pricing */}
              <div className="flex items-center justify-between p-4 bg-gray-900 rounded-lg">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="editTrackIsFree"
                    checked={editTrackIsFree}
                    onChange={(e) => setEditTrackIsFree(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500"
                  />
                  <label htmlFor="editTrackIsFree" className="text-white font-medium">Free Download</label>
                </div>
                {!editTrackIsFree && (
                  <div className="flex items-center gap-2">
                    <DollarSign size={18} className="text-gray-400" />
                    <input
                      type="number"
                      value={editTrackPrice}
                      onChange={(e) => setEditTrackPrice(e.target.value)}
                      placeholder="29.99"
                      className="w-24 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 text-sm"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAudioEditModal(false)}
                className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                disabled={isUpdatingTrack}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateTrack}
                disabled={isUpdatingTrack || !editTrackTitle}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg transition-all font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingTrack ? (
                  <><Loader2 size={18} className="animate-spin" /> Saving...</>
                ) : (
                  <><Check size={18} /> Save Changes</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Upload Modal */}
      {showPhotoUploadModal && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" onClick={() => resetPhotoUploadModal()}>
          <div className="bg-black border-2 border-gray-600 rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Camera className="text-blue-400" size={24} />
                Upload Photos
              </h2>
              <button onClick={() => resetPhotoUploadModal()} className="text-gray-400 hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>

            {/* Photo File Upload */}
            <div 
              className="border-2 border-dashed border-gray-600 rounded-xl p-6 mb-4 text-center hover:border-blue-500 transition-colors cursor-pointer"
              onClick={() => photoInputRef.current?.click()}
            >
              <input
                ref={photoInputRef}
                type="file"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setSelectedPhotoFiles(files);
                }}
                className="hidden"
                accept=".jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.bmp,.tiff"
                multiple
              />
              {selectedPhotoFiles.length > 0 ? (
                <>
                  <Image className="mx-auto text-blue-400 mb-3" size={48} />
                  <p className="text-white font-semibold mb-1">{selectedPhotoFiles.length} photo(s) selected</p>
                  <p className="text-gray-400 text-sm">
                    {selectedPhotoFiles.map(f => f.name).slice(0, 3).join(', ')}
                    {selectedPhotoFiles.length > 3 && ` +${selectedPhotoFiles.length - 3} more`}
                  </p>
                </>
              ) : (
                <>
                  <Upload className="mx-auto text-gray-400 mb-3" size={48} />
                  <p className="text-white font-semibold mb-1">Drop photos here or click to browse</p>
                  <p className="text-gray-400 text-sm">JPG, PNG, GIF, WEBP supported • Multiple files allowed</p>
                </>
              )}
            </div>

            {/* Photo Preview Grid */}
            {selectedPhotoFiles.length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-4">
                {selectedPhotoFiles.slice(0, 8).map((file, idx) => (
                  <div key={idx} className="aspect-square bg-gray-800 rounded-lg overflow-hidden">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Preview ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
                {selectedPhotoFiles.length > 8 && (
                  <div className="aspect-square bg-gray-800 rounded-lg flex items-center justify-center">
                    <span className="text-gray-400 text-sm font-medium">+{selectedPhotoFiles.length - 8}</span>
                  </div>
                )}
              </div>
            )}

            {/* Category Selection */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-400 mb-2">Category</label>
              <select
                value={photoCategory}
                onChange={(e) => setPhotoCategory(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                {photoCategories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Album (Optional) */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-400 mb-2">Album (Optional)</label>
              <input
                type="text"
                value={photoAlbum}
                onChange={(e) => setPhotoAlbum(e.target.value)}
                placeholder="e.g., Summer 2026, Product Shots"
                className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => resetPhotoUploadModal()}
                className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                disabled={isUploadingPhotos}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadPhotos}
                disabled={isUploadingPhotos || selectedPhotoFiles.length === 0}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploadingPhotos ? (
                  <><Loader2 size={18} className="animate-spin" /> Uploading...</>
                ) : (
                  <><Upload size={18} /> Upload {selectedPhotoFiles.length} Photo{selectedPhotoFiles.length !== 1 ? 's' : ''}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Edit Modal */}
      {showPhotoEditModal && editingPhoto && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" onClick={() => setShowPhotoEditModal(false)}>
          <div className="bg-black border-2 border-gray-600 rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Edit Photo</h2>
              <button onClick={() => setShowPhotoEditModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            {/* Photo Preview */}
            <div className="mb-4">
              <img
                src={`${API_URL}${editingPhoto.photo_url}`}
                alt={editingPhoto.title || 'Photo'}
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>

            {/* Title */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-400 mb-2">Title</label>
              <input
                type="text"
                value={editPhotoTitle}
                onChange={(e) => setEditPhotoTitle(e.target.value)}
                placeholder="Photo title"
                className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* Caption */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-400 mb-2">Caption</label>
              <textarea
                value={editPhotoCaption}
                onChange={(e) => setEditPhotoCaption(e.target.value)}
                placeholder="Photo caption or description"
                rows={3}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none"
              />
            </div>

            {/* Tags */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-400 mb-2">Tags (comma separated)</label>
              <input
                type="text"
                value={editPhotoTags}
                onChange={(e) => setEditPhotoTags(e.target.value)}
                placeholder="portrait, studio, black and white"
                className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* Category */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-400 mb-2">Category</label>
              <select
                value={editPhotoCategory}
                onChange={(e) => setEditPhotoCategory(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                {photoCategories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between mt-6">
              <button
                onClick={() => {
                  if (window.confirm('Delete this photo permanently?')) {
                    handleDeletePhoto(editingPhoto.id);
                  }
                }}
                className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Trash2 size={16} />
                Delete
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPhotoEditModal(false)}
                  className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdatePhoto}
                  disabled={isUpdatingPhoto}
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all font-semibold flex items-center gap-2 disabled:opacity-50"
                >
                  {isUpdatingPhoto ? (
                    <><Loader2 size={16} className="animate-spin" /> Saving...</>
                  ) : (
                    <><Check size={16} /> Save Changes</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Manager Modal */}
      {showCategoryManager && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" onClick={() => setShowCategoryManager(false)}>
          <div className="bg-black border-2 border-gray-600 rounded-xl max-w-md w-full p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Manage Categories</h2>
              <button onClick={() => setShowCategoryManager(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            {/* Add New Category */}
            <div className="flex gap-2 mb-6">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="New category name"
                className="flex-1 px-4 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleAddPhotoCategory()}
              />
              <button
                onClick={handleAddPhotoCategory}
                disabled={!newCategoryName.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Plus size={18} />
                Add
              </button>
            </div>

            {/* Category List */}
            <div className="space-y-2">
              {photoCategories.map((category, index) => (
                <div
                  key={category.id}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('dragIndex', index.toString())}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const dragIndex = parseInt(e.dataTransfer.getData('dragIndex'));
                    if (dragIndex !== index) {
                      handleReorderPhotoCategories(dragIndex, index);
                    }
                  }}
                  className="flex items-center gap-3 p-3 bg-gray-900 border border-gray-700 rounded-lg group hover:border-gray-600 cursor-grab active:cursor-grabbing"
                >
                  <span className="text-gray-600 cursor-grab">⋮⋮</span>
                  
                  {editingCategoryId === category.id ? (
                    <input
                      type="text"
                      value={editingCategoryName}
                      onChange={(e) => setEditingCategoryName(e.target.value)}
                      className="flex-1 px-3 py-1 bg-gray-800 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
                      autoFocus
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleUpdatePhotoCategory(category.id, editingCategoryName);
                        }
                      }}
                      onBlur={() => {
                        if (editingCategoryName.trim() && editingCategoryName !== category.name) {
                          handleUpdatePhotoCategory(category.id, editingCategoryName);
                        } else {
                          setEditingCategoryId(null);
                        }
                      }}
                    />
                  ) : (
                    <span className="flex-1 text-white">{category.name}</span>
                  )}
                  
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => {
                        setEditingCategoryId(category.id);
                        setEditingCategoryName(category.name);
                      }}
                      className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded transition-colors"
                      title="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete "${category.name}" category?`)) {
                          handleDeletePhotoCategory(category.id);
                        }
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-500 mt-4 text-center">Drag to reorder categories. Changes are saved automatically.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealEstateMediaPortfolio;
